<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    reactive,
    ref,
    watch,
} from "vue"
import { useI18n } from "vue-i18n"
import { useRoute, useRouter } from "vue-router"
import { storeToRefs } from "pinia"
import { formatIdSource } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useSourceStore } from "@core/stores/source.store"
import { track } from "@core/tracking/track"
import { useOptimTurpeV2Store } from "@modules/courtage/stores/optim-turpe-v2.store"
import { optimTurpeV2MockEtudes } from "@modules/courtage/stores/optim-turpe-v2.mock-data"
import { getSourceSegmentForOptim } from "@modules/courtage/utils/resolveSourceSegmentForOptim"
import { RAPPORT_PDF_V2_ACTIF } from "@modules/courtage/constants/optim-turpe-v2"
import { lireChiffre } from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import {
    libelleCollecteArretee,
    lireEchec,
    saisiesDuLancement,
    type SaisiesDuLancement,
} from "@modules/courtage/utils/optim-turpe-v2.echec"
import {
    attenteDesDonnees,
    refusDeLaCollecte,
} from "@modules/courtage/utils/optim-turpe-v2.collecte"
import { rapportProposable } from "@modules/courtage/utils/optim-turpe-v2.rapport"
import {
    dateDeCalcul,
    corpsDeRelance,
    aUneEtudeAboutie,
    grouperParLancement,
    recommandationARendre,
    type RecommandationARendre,
    lancementsEnAttenteAbandonnes,
    relanceDuLancement,
    surchargesDeRejeu,
} from "@modules/courtage/utils/optim-turpe-v2.cycle"
import {
    lireCarteDeTete,
    resumeCompose,
} from "@modules/courtage/utils/optim-turpe-v2.carte"
import {
    MESSAGE_REFUS_POSTES,
    postesAbsentsRefuses,
    textesDesPostes,
} from "@modules/courtage/utils/optim-turpe-v2.fenetre"
import type { PeriodeProposee } from "@modules/courtage/utils/optim-turpe-v2.fenetre"
import { segmentNormalise } from "@modules/courtage/utils/optim-turpe-v2.saisie"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import {
    formatDateFr,
    formatDateHeureParis,
    getNiveauDonnees,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import { verdictSegment } from "@modules/courtage/utils/optim-turpe-v2.perimetre"
import type { OptimTurpeV2RunBody } from "@modules/courtage/services/optim-turpe-v2.service"
import type { OptimSegment } from "@core/tracking/events"
import { useAuthStore } from "@core/stores/auth.store"
import { conseilEtabli } from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import {
    enteteAgir,
    enteteCalcul,
    enteteChoisir,
    enteteEtudes,
    entetePartager,
    entetePostes,
    entetePourquoi,
    enteteResume,
    etatDuParcours,
    explicationRedigee,
    noyauComposantes,
    noyauDepassements,
    noyauHypotheses,
    noyauPourquoi,
    noyauRapport,
    presentationDuTemps2,
    resumeDuRapport,
    valeursDuBandeau,
    valeursParDefautNommees,
    type ContexteEntetes,
    type Entete,
} from "@modules/courtage/utils/optim-turpe-v2.entetes"
import { remedesDuRefus } from "@modules/courtage/utils/optim-turpe-v2.completer"
import { MARGE_FRACTION_PAR_DEFAUT } from "@modules/courtage/utils/optim-turpe-v2.marge"
import { lireDemande } from "@modules/courtage/utils/optim-turpe-v2.demande"
import {
    cleDeMemoire,
    ecrireMemoire,
    lireMemoire,
    lireParametreOuvrir,
    memoireVide,
    ouverturesInitiales,
    rangeeDeLAncre,
    rangeesOuvertesInitiales,
    SECTIONS_MEMORISEES,
    type RangeeDuSommaire,
    type MemoireOuvertures,
    type SectionAccompagnement,
    type TempsRepliable,
} from "@modules/courtage/utils/optim-turpe-v2.ouvertures"
import {
    forcesDeLaRegle,
    regleDeParcours,
} from "@modules/courtage/utils/optim-turpe-v2.parcours"
import OptimTurpeV2Bandeau, {
    type EtapeDuFil,
} from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2Bandeau.vue"
import OptimTurpeV2Cadre from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2Cadre.vue"
import OptimTurpeV2Section from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2Section.vue"
import OptimTurpeV2RapportEtDemande from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2RapportEtDemande.vue"
import OptimTurpeV2CarteDeTete from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2CarteDeTete.vue"
import OptimTurpeV2Comparaison from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2Comparaison.vue"
import OptimTurpeV2PointsAVerifier from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2PointsAVerifier.vue"
import OptimTurpeV2TexteSigne from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2TexteSigne.vue"
import {
    pointsDActions,
    resumeDesPointsDActions,
} from "@modules/courtage/utils/optim-turpe-v2.points"
import {
    lignesDuPourquoi,
    pucesDuPourquoi,
} from "@modules/courtage/utils/optim-turpe-v2.tableau"
import { regleCroissanceDesPostes } from "@modules/courtage/utils/optim-turpe-v2.lexique"
import { useOptimTurpeV2DerniereEtudeV1 } from "@modules/courtage/composables/useOptimTurpeV2DerniereEtudeV1"
import {
    lireComparaisonV1,
    comparaisonVisible,
    noyauDeLEntete,
} from "@modules/courtage/utils/optim-turpe-v2.pont"
import { estSessionDeLEquipe } from "@modules/courtage/utils/optim-turpe-v2.equipe"
import {
    TEXTES_RETOURS,
    cleJournalRetours,
    journalVide,
    lireJournal,
    noterEnvoi,
    reponsesMesurees,
    type JournalRetours,
    type ReponsesRetours,
    type VarianteRetours,
} from "@modules/courtage/utils/optim-turpe-v2.retours"
import OptimTurpeV2Retours from "@modules/courtage/components/optimturpe-v2/OptimTurpeV2Retours.vue"
import { routeV2Query } from "@modules/courtage/utils/optimturpe-v2.acces"
import { segmentDeLAdresse } from "@modules/courtage/utils/optim-turpe-v2.aide.segment"
import {
    compteurEnAttenteDeConsentement,
    MENTION_CONSENTEMENT_EN_ATTENTE,
} from "@modules/courtage/utils/optim-turpe-v2.consentement"
import { libelleOptionCompteur } from "@modules/courtage/utils/optim-turpe-v2.libelles"
import { cheminsSurcharges } from "@modules/courtage/utils/optim-turpe-v2.mesure"
import {
    NIVEAU_2,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            // « v2 » est un détail d'implémentation interne (cohabitation v1/v2) — pas exposé au client.
            title: "Optimisation du TURPE",
            // W1 (vague wording du 14/10/2026) : le sigle expliqué une fois.
            sousTitre:
                "Puissances souscrites et formule d'acheminement (FTA) : la part acheminement de la facture d'électricité",
            selectorLabel: "Compteur",
            empty: "Sélectionnez un compteur pour lancer l'étude.",
            // Le périmètre est celui que le MOTEUR sert (DEV-1912) : la page ne
            // recopie plus « C5/C4/C2 », qui divergeait de sa propre règle de clic.
            noMeter: "Aucun compteur électrique sur ce bâtiment.",
            actions: {
                // D24, maquette mq-rapport : les deux gestes du temps 3, dans
                // son en-tête, visibles au repos.
                rapport: "Aperçu du rapport",
                telecharger: "Télécharger le rapport",
                composition: "Composition…",
            },
            rapportDialogTitle: "Aperçu du rapport",
            // DEV-2439 : une relance refusée ne fait pas perdre la
            // recommandation d'avant ; la page la rend, sans recalcul.
            // Retours du 23/10/2026 : la période proposée qui serait refusée
            // pour postes absents ne part pas ; la page le dit avec la phrase
            // du panneau « Affiner l'étude ».
            periodeProposee: {
                refusPostes: MESSAGE_REFUS_POSTES,
            },
            recommandationPrecedente: {
                texte: "La recommandation du {date} reste disponible.",
                revenir: "Revenir à cette recommandation",
                aucune: "Aucune recommandation n'a encore abouti sur ce compteur. Pour en obtenir une, lancez une nouvelle étude.",
            },
            perimetre: {
                // Q28 : sans « encore », qui promettait une couverture.
                horsPerimetreDefaut:
                    "L'étude ne couvre pas ce segment de compteur.",
                inconnu: "",
            },
            rapportApercuChargement: "Composition du document…",
            // Revue sceptique de L-D (D26) : le document ne s'est pas chargé
            // dans le délai de garde ; le geste reste dans le pied.
            rapportApercuEnRetard:
                "L'aperçu ne s'affiche pas : téléchargez le rapport ou ouvrez-le dans un nouvel onglet.",
            rapportApercuTitre: "Rapport d'optimisation TURPE",
            rapportOuvrirOnglet: "Ouvrir dans un nouvel onglet",
            // W64 : « environnement » est un mot technique.
            // E10 : posé sur une fonction absente de l'environnement, sans
            // nouvel essai possible avant un rechargement : aucune invitation
            // à réessayer.
            rapportIndisponible:
                "Le rapport n'est pas disponible pour le moment.",
            // DEV-2164, cas 3 ; texte de la maquette mq-rapport (D24). Le
            // bêta-test a mesuré environ deux secondes de composition.
            rapportEnCours:
                "Composition du document, quelques secondes. Vous pouvez continuer à lire la page.",
            rapportRetenter: "Réessayer",
            // DEV-2156 : l'étude affichée à l'ouverture est celle déjà calculée.
            // DEV-2311 : la page d'aide de l'étude (maquette v0.3.2).
            comprendre: "Comprendre l'étude",
            // R4 (revue du 08/10) : la dernière étude du compte est ailleurs.
            etudeAilleurs:
                "La dernière étude de votre compte est sur le bâtiment",
            // DEV-2284 (lot A) : le cadre en tête, puis quatre temps.
            // Vague wording du 14/10/2026 (W17, Q36) : les sections sans
            // numéro, nommées par leur objet : « Hypothèses · Résultat ·
            // Dépassements · Rapport et demande · Études de ce compteur ».
            parcours: {
                t1Titre: "Résultat",
                // Le titre et la question de « Dépassements » :
                // `presentationDuTemps2`.
                t3Titre: "Rapport et demande",
                t3Question:
                    "Le rapport à remettre, puis ce qu'il faut demander au fournisseur",
                t1SansObjet: "Après une étude aboutie",
                filCadre: "Hypothèses",
                filT1: "Résultat",
                filT3: "Rapport et demande",
                filEtudes: "Études de ce compteur",
            },
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const router = useRouter()
const optimTurpeV2Store = useOptimTurpeV2Store()
const buildingStore = useBuildingStore()
const sourceStore = useSourceStore()

/**
 * La page d'aide « Comprendre l'étude » (DEV-2311), sur le compteur affiché :
 * son retour ramène au même compteur. Adresse par le chemin, sous la même
 * garde que cette page (`building.routes.ts`).
 */
const getRouteAide = computed(() => {
    // N7 (contrat v1.3, §9, lot 4i) : l'aide parle du segment du compteur
    // affiché ; jamais de PDL ni de site dans l'adresse.
    const segment = segmentDeLAdresse(getSelectedSegment.value)
    return {
        path: `/building/${String(route.params._id_building ?? "")}/optimisation-contractuelle-v2/aide`,
        query: {
            ...routeV2Query(optimTurpeV2Store.currentSourceId),
            ...(segment ? { segment } : {}),
        },
    }
})
const { isFetchingBuilding, isFetchingBuildingSources } =
    storeToRefs(buildingStore)

// ================================
// PROPS
// ================================
/**
 * `etudeId` — la fixture à afficher en mode mock (Storybook seulement ; la route
 * ne passe aucune prop).
 *
 * Défaut corrigé (revue C2 du 23/09) : la page fixait la fixture à
 * `mock-c4-0001` et la re-posait dans `onMounted`, APRÈS que le décorateur de
 * story avait positionné celle que la story demandait. Les neuf stories
 * rendaient donc toutes la même étude C4 en économie. La story dit désormais ce
 * qu'elle veut voir par cette prop, et la page ne réécrit jamais une étude déjà
 * positionnée.
 */
const props = withDefaults(defineProps<{ etudeId?: string }>(), {
    etudeId: "",
})

// ================================
// STATE
// ================================
/** Mock-first (Storybook) : id de la fixture sélectionnée. */
const selectedEtudeId = ref<string>(props.etudeId || "mock-c4-0001")
const isRapportVisible = ref<boolean>(false)
/** D26 : le document de l'aperçu est-il chargé ? Jusque-là, l'attente le couvre. */
const apercuCharge = ref<boolean>(false)
/**
 * Revue sceptique de L-D (D26) : si le document ne se charge pas (URL expirée,
 * document refusé, cadre bloqué), l'événement `load` ne part jamais. Passé ce
 * délai de garde, l'attente se retire et la page dit quoi faire, au lieu de
 * rester sur « Composition du document… » pour toujours.
 */
const APERCU_DELAI_DE_GARDE_MS = 8000
const apercuEnRetard = ref<boolean>(false)
let gardeApercu: ReturnType<typeof setTimeout> | null = null
function arreterGardeApercu(): void {
    if (gardeApercu !== null) clearTimeout(gardeApercu)
    gardeApercu = null
}
watch(
    () => optimTurpeV2Store.reportUrl,
    () => {
        apercuCharge.value = false
    }
)
watch(
    () =>
        [
            isRapportVisible.value,
            optimTurpeV2Store.reportUrl,
            apercuCharge.value,
        ] as const,
    ([visible, url, charge]) => {
        arreterGardeApercu()
        apercuEnRetard.value = false
        if (!visible || !url || charge) return
        gardeApercu = setTimeout(() => {
            gardeApercu = null
            if (!apercuCharge.value) apercuEnRetard.value = true
        }, APERCU_DELAI_DE_GARDE_MS)
    }
)
/**
 * Le champ que le client a demandé à compléter (L29) — depuis un refus nommé ou
 * depuis une ligne du registre des entrées. La page ne fait que transmettre la
 * demande à « Affiner l'étude », qui ouvre le champ. Elle ne remplit rien.
 */
const ancreACompleter = ref<AncreAffiner | null>(null)

// ================================
// COMPUTED
// ================================
/** Mode mock (Storybook) vs réel (app branchée api-metier). */
const isMock = computed(() => optimTurpeV2Store.useMock)
/**
 * R4 (revue du 08/10) : la page d'un bâtiment ne rend JAMAIS le compteur ni
 * l'étude d'un autre bâtiment, même un instant. Le magasin survit à la
 * navigation ; s'il porte un autre bâtiment, il se vide ici, avant le premier
 * rendu et avant toute lecture (dont celle de l'ancienne version du compteur).
 */
if (!isMock.value) optimTurpeV2Store.entrerSurBatiment(buildingId())
const getCurrentEtude = computed(() => optimTurpeV2Store.currentEtude)
const getIsLoading = computed(
    () =>
        optimTurpeV2Store.isRunning ||
        isFetchingBuilding.value ||
        isFetchingBuildingSources.value
)
const getError = computed(() => optimTurpeV2Store.runError)
/**
 * Le refus NOMMÉ du dernier lancement (DEV-2163), ou `null`. Tant qu'il est là,
 * l'en-tête de l'étude d'avant est retiré, les réglages restent ouverts sur la
 * période demandée, et le remède est dit à côté de la cause.
 */
const getRefus = computed(() => optimTurpeV2Store.refus)
/** L'étude sur laquelle les réglages se pré-remplissent pendant un refus. */
const getEtudeReglages = computed(() => getCurrentEtude.value ?? getRefus.value)
/** « 13/10/2026 » : la date de l'étude, sur la carte de tête. */
const getDateEtude = computed(() => {
    const date = getCurrentEtude.value
        ? dateDeCalcul(getCurrentEtude.value)
        : null
    return date ? formatDateFr(date) : null
})
/** « Étude calculée le 25/09/2026 à 14:55 » (DEV-2156), ou `null`. */
const getCalculeeLe = computed(() => {
    const date = getCurrentEtude.value
        ? dateDeCalcul(getCurrentEtude.value)
        : null
    return date ? formatDateHeureParis(date) : null
})
/**
 * État d'ATTENTE, servi en information et non en erreur (DEV-1529) : « calcul en
 * cours » pendant que le moteur travaille en file, puis le constat du plafond de
 * suivi. Un calcul qui prend du temps n'est pas une panne, et l'écran ne doit pas
 * le peindre en rouge.
 */
const getMessageAttente = computed(() => optimTurpeV2Store.messageAttente)

/**
 * Compteurs électriques du bâtiment. La page ne filtre PLUS sur sa propre règle
 * d'éligibilité (DEV-1678) : c'est le moteur qui dit ce qu'il couvre, et le refus
 * nommé qui tranche un cas qu'il ne couvre pas. Seul le type de source reste un
 * filtre de la page, parce qu'un compteur gaz n'a rien à faire dans une étude
 * TURPE électricité.
 */
const getEligibleMeters = computed(() =>
    buildingStore.getMeters.filter((meter) => meter.type === SOURCES_TYPES.PDL)
)

/** Le périmètre servi par le moteur — `null` tant que la façade ne le relaie pas. */
const getPerimetre = computed(() => optimTurpeV2Store.perimetre)

/**
 * Segment d'un compteur du bâtiment, tel que sa source le porte, ou `null`.
 * Il se lit sur le compteur VISÉ par le geste, jamais sur celui encore affiché :
 * au passage d'un C4 à un C5, l'écran montre encore le C4 quand le lancement
 * part (relecture de #663).
 */
function segmentDe(sourceId: string): string | null {
    const meter = getEligibleMeters.value.find((m) => m._id_source === sourceId)
    return meter ? getSourceSegmentForOptim(meter) || null : null
}

/** Segment du compteur sélectionné, tel que la source le porte. */
const getSelectedSegment = computed(
    () => segmentDe(optimTurpeV2Store.currentSourceId) ?? ""
)

/**
 * Le segment du compteur sélectionné est-il couvert ? Trois réponses, et la
 * troisième compte : `null` = périmètre inconnu, on ne pré-annonce rien et on
 * laisse lancer l'étude.
 */
const getVerdictSegment = computed(() =>
    verdictSegment(getPerimetre.value, getSelectedSegment.value || null)
)

/** Le fait, dit AVANT le clic, avec le libellé SERVI par le moteur (A16). */
const getHorsPerimetreLabel = computed(() => {
    const verdict = getVerdictSegment.value
    if (verdict.couvert !== false) return ""
    return verdict.libelle ?? t("perimetre.horsPerimetreDefaut")
})

/** L'échec nommé de l'étude affichée, ou `null`. */
const getEchec = computed(() =>
    getCurrentEtude.value ? lireEchec(getCurrentEtude.value) : null
)

/**
 * Le rapport (téléchargement et aperçu, à l'en-tête du temps 3) est MASQUÉ tant
 * que la route de rendu n'existe pas (F8.1), pendant qu'une erreur est à
 * l'écran (F7.16), et tant que l'étude n'a pas de résultat : le moteur refuse
 * sinon (D24, maquette mq-rapport).
 */
const getRapportProposable = computed(() =>
    rapportProposable(getCurrentEtude.value, {
        actif: RAPPORT_PDF_V2_ACTIF,
        erreur: Boolean(getError.value),
        echec: getEchec.value !== null,
        refus: Boolean(getRefus.value),
    })
)

/** Segment au vocabulaire du plan de mesure, ou `undefined` (jamais deviné). */
function segmentPourMesure(): OptimSegment | undefined {
    const segment = getSelectedSegment.value
    return segment ? (segment as OptimSegment) : undefined
}

function buildingId(): string {
    return String(route.params._id_building ?? "")
}

/** Options du sélecteur : fixtures mock (Storybook) ou vrais compteurs du bâtiment. */
const getMeterOptions = computed(() =>
    isMock.value
        ? Object.entries(optimTurpeV2MockEtudes).map(([id, etude]) => ({
              label: libelleOptionCompteur(
                  etude.compteur?.segment,
                  etude.source?.idsource
                      ? formatIdSource(etude.source.idsource, "pdl")
                      : ""
              ),
              value: id,
          }))
        : getEligibleMeters.value.map((meter) => {
              // Finitions 2 : un segment inconnu ne s'écrit plus « - — ».
              const base = libelleOptionCompteur(
                  getSourceSegmentForOptim(meter),
                  formatIdSource(meter.idsource, meter.idtype)
              )
              // DEV-2189 : un compteur à collecte arrêtée le dit, daté, avant
              // le clic (date servie dans `error.details` de son refus).
              const arret = libelleCollecteArretee(
                  optimTurpeV2Store.refusConnus[meter._id_source]
              )
              // Retour du 07/10 : un compteur listé au seul titre d'une
              // demande de consentement non signée le dit, après son numéro.
              const attente = compteurEnAttenteDeConsentement(meter)
                  ? `, ${MENTION_CONSENTEMENT_EN_ATTENTE}`
                  : ""
              return {
                  label: `${base}${attente}${arret ? `, ${arret}` : ""}`,
                  value: meter._id_source,
              }
          })
)

/**
 * Le compteur sélectionné n'est listé qu'au titre d'une demande de
 * consentement non signée : sa collecte n'est pas active, l'étude ne
 * trouverait aucune donnée. Sans étude, le bloc des paramètres le dit au lieu
 * de proposer le lancement (retour de la campagne du 07/10/2026).
 */
const getConsentementEnAttente = computed(
    () =>
        !isMock.value &&
        compteurEnAttenteDeConsentement(
            getEligibleMeters.value.find(
                (m) => m._id_source === optimTurpeV2Store.currentSourceId
            )
        )
)

/**
 * Reco (p), lot L4 : les données du contrat du compteur sélectionné ne sont
 * pas reçues, et le moteur refuserait l'étude (même critère que
 * `raise_si_collecte_empeche_la_fta`, `optim-turpe-v2.collecte.ts`). La carte
 * le dit en mode `entree` ; un lancement sans formule ni segment saisis ne
 * part pas. La carte du consentement garde la priorité.
 */
function donneesEnAttente(saisies: SaisiesDuLancement = {}) {
    if (isMock.value || getConsentementEnAttente.value) return null
    return attenteDesDonnees(
        getEligibleMeters.value.find(
            (m) => m._id_source === optimTurpeV2Store.currentSourceId
        ),
        saisies
    )
}
const getDonneesEnAttente = computed(() => donneesEnAttente())
/** Ce que le formulaire doit saisir pour lever le refus (pied du formulaire). */
const getDonneesManquantes = computed(() => {
    if (!getDonneesEnAttente.value) return null
    const refus = refusDeLaCollecte(
        getEligibleMeters.value.find(
            (m) => m._id_source === optimTurpeV2Store.currentSourceId
        )
    )
    return refus === "compteur-identifiant-inconnu"
        ? "formule"
        : "formule-ou-segment"
})

/** La fiche du compteur : la page des compteurs du bâtiment, si la route existe. */
const getLienCompteur = computed(() =>
    router.hasRoute("PageBuildingMeter")
        ? {
              name: "PageBuildingMeter",
              params: { _id_building: buildingId() },
          }
        : null
)

/** Valeur courante du sélecteur (id fixture en mock, `_id_source` en réel). */
const getSelectedValue = computed(() =>
    isMock.value ? selectedEtudeId.value : optimTurpeV2Store.currentSourceId
)

/**
 * Un seul compteur éligible → pas de vrai choix : on affiche le compteur en clair plutôt
 * qu'un sélecteur trompeur à une seule option (cas courant, parc mono-PDL — DEV-783).
 */
const getHasMultipleMeters = computed(() => getMeterOptions.value.length > 1)
const getSingleMeterLabel = computed(
    () => getMeterOptions.value[0]?.label ?? ""
)

// ================================
// LE PARCOURS (DEV-2284, lot A) : le cadre en tête, puis quatre temps.
// Le lecteur lit sur quoi porte le chiffre, puis le chiffre, puis décide,
// puis partage ; chaque section repliée dit sa réponse (en-têtes porteurs,
// calculés par une fonction pure depuis les clés servies).
// ================================
/** Ce que les en-têtes lisent : l'étude affichée, le refus, la paire. */
const getContexte = computed<ContexteEntetes>(() => {
    const sans = optimTurpeV2Store.etudeSansCmdps
    const avec = optimTurpeV2Store.etudeAvecCmdps
    const pertinent = optimTurpeV2Store.getArbitragePertinent
    return {
        etude: getCurrentEtude.value,
        refus: getRefus.value,
        sans,
        avec,
        arbitragePertinent: pertinent,
        retenu: optimTurpeV2Store.scenarioRetenu,
        // Revue de nuit du 15/10/2026 (E6) : la carte et l'en-tête
        // « Dépassements » ne disent « recommandé » que sur un conseil ÉTABLI,
        // comme l'historique ; le scénario affiché par défaut reste « Couvrir ».
        conseil: pertinent ? conseilEtabli(sans, avec) : null,
    }
})
/**
 * Revue 2 en production (I24, I61, DEV-2404) : le scénario affiché n'est pas
 * le recommandé (conseil ÉTABLI seulement, comme la carte) ; le graphique
 * garde alors sa colonne, nommée « Option chiffrée ».
 */
const getScenarioNonRecommande = computed(() => {
    const { arbitragePertinent, retenu, conseil } = getContexte.value
    return Boolean(
        arbitragePertinent && retenu && conseil && retenu !== conseil
    )
})
const getEtatParcours = computed(() => etatDuParcours(getContexte.value))
const getEnteteT2 = computed(() => enteteChoisir(getContexte.value))
/** R6 : « Choisir le scénario », ou « Un seul scénario » et pourquoi. */
const getPresentationT2 = computed(() =>
    presentationDuTemps2(getContexte.value)
)
const getEnteteT3 = computed(() => entetePartager(getContexte.value))
const getEnteteT4 = computed(() => enteteAgir(getContexte.value))
/**
 * La carte de tête du résultat (vague wording du 14/10/2026, Q16, Q17) :
 * titre par état, montant signé, composition, raison, valeurs à demander,
 * alerte, une action.
 */
const getCarte = computed(() =>
    getModeCadre.value === "etude"
        ? lireCarteDeTete(getCurrentEtude.value, getContexte.value)
        : null
)
const getEnteteCalcul = computed(() =>
    enteteCalcul(getRefus.value ? null : getCurrentEtude.value)
)
const getEntetePostes = computed(() =>
    entetePostes(getCurrentEtude.value, getEtatParcours.value, {
        // Série UX du témoin (2.3 point 4) : la carte de tête dit déjà la
        // composition ; l'en-tête de l'accordéon ne la redit pas.
        compositionDansLaCarte:
            (getCarte.value?.fiche.composition.length ?? 0) > 0,
    })
)
const getEntetePourquoi = computed(() =>
    entetePourquoi(getCurrentEtude.value, getEtatParcours.value)
)

/**
 * D29 : la dernière étude de l'ancienne version du compteur, lue par sa route
 * existante (lecture seule), et l'encadré de comparaison qui en découle, sous
 * le montant, replié. Sans étude ancienne, l'encadré est absent.
 */
const { derniere: derniereEtudeV1 } = useOptimTurpeV2DerniereEtudeV1(
    computed(() => (isMock.value ? "" : optimTurpeV2Store.currentSourceId))
)
const getComparaisonV1 = computed(() =>
    lireComparaisonV1({
        v1: derniereEtudeV1.value,
        etude: getCurrentEtude.value,
        sans: optimTurpeV2Store.etudeSansCmdps,
        avec: optimTurpeV2Store.etudeAvecCmdps,
    })
)
/**
 * K2 (vague wording du 14/10/2026) : l'ancienne version ne s'affiche plus
 * pendant le calcul, seulement avec le résultat (« Comparaison avec
 * l'ancienne version »).
 */
const getEnteteComparaison = computed<Entete>(() => {
    const c = getComparaisonV1.value
    // D8 (a) : au client sur raison prouvée, toujours à l'équipe.
    const visible = comparaisonVisible(c, estSessionDeLEquipe())
    return {
        forme: c && visible ? "repliee" : "absente",
        libelle: "Comparaison avec l'ancienne version",
        noyau: c && visible ? noyauDeLEntete(c) : null,
        complement: null,
        pastille: null,
        pastilleReserve: false,
    }
})
/**
 * Le « pourquoi » servi, tel quel : il ouvre « Pourquoi ces puissances »
 * (maquette allégée, fusion avec l'ancienne section « L'explication »).
 */
const getExplication = computed(() => explicationRedigee(getCurrentEtude.value))
/**
 * Le résumé pour le client final (Q40, W69) : composé à partir des valeurs
 * servies, impersonnel ; le texte rédigé servi n'est plus qu'un repli.
 */
const getResume = computed(() =>
    getEtatParcours.value === "refus" ||
    getEtatParcours.value === "montant_non_servi"
        ? null
        : (resumeCompose(getCurrentEtude.value, getContexte.value) ??
          resumeDuRapport(getCurrentEtude.value))
)
const getEnteteResume = computed(() => enteteResume(getResume.value))
/** La carte propose-t-elle une démarche (« Préparer la demande ») ? */
const getAvecDemarche = computed(
    () =>
        getCarte.value?.action === "preparer_demande" ||
        getCarte.value?.secondaire === "preparer_demande"
)
/** La signature du courriel, préremplie depuis la session (C28). */
const getSignataire = computed(() => {
    const auth = useAuthStore()
    const nom = [auth.firstname, auth.lastname]
        .map((v) => (typeof v === "string" ? v.trim() : ""))
        .filter(Boolean)
        .join(" ")
    return nom || null
})
const getSociete = computed(() => {
    const nom = useAuthStore().companyName
    return typeof nom === "string" && nom.trim() ? nom.trim() : null
})
/** Le bloc du courriel déplié (ancre #rapport depuis « Préparer la demande »). */
const courrielOuvert = ref(false)

/** Les valeurs prises par défaut, nommées : la marche à suivre les fait vérifier. */
const getValeursParDefaut = computed(() =>
    valeursParDefautNommees(getCurrentEtude.value)
)
/** « Ce que l'étude ne prend pas en compte » existe sous un dossier de demande. */
const getAvecLimites = computed(() => {
    const etude = getCurrentEtude.value
    if (!etude) return false
    const demande = lireDemande(etude)
    return demande !== null
})
/** Le site, nommé dans le texte copié de la demande ; absent, il n'est pas écrit. */
const getSite = computed(() => buildingStore.building?.name ?? null)
const getEnteteEtudes = computed(() =>
    enteteEtudes(
        optimTurpeV2Store.historique,
        getRefus.value ? null : (getCurrentEtude.value?._id ?? null),
        optimTurpeV2Store.historiqueTronque
    )
)

/** Le point de livraison écrit comme partout ailleurs sur la page. */
const getPdlAffiche = computed(() => {
    const id = (getRefus.value ?? getCurrentEtude.value)?.source?.idsource
    return id ? formatIdSource(id, "pdl") : null
})
const getValeursBandeau = computed(() =>
    valeursDuBandeau(getContexte.value, getPdlAffiche.value)
)

/** Les ouvertures de la page. Le cadre n'est gardé nulle part (geste vers un recalcul). */
const ouvertures = reactive<
    Record<"cadre" | TempsRepliable | SectionAccompagnement, boolean>
>({
    cadre: false,
    t2: false,
    t3: false,
    t4: false,
    remise: false,
    demarche: false,
})
/** Les sections de détail : la mémoire de la page, pour ce compteur et cette visite. */
const detailsOuverts = reactive<Record<string, boolean>>({})

// ================================
// LE SOMMAIRE DU RÉSULTAT (maquette finale du 15/10/2026, DEV-2404) : huit
// rangées en trois accordéons, repliées au repos, chacune titrée et résumée.
// ================================
/** Les rangées ouvertes des trois accordéons (une seule liste partagée). */
const ouvertes = ref<RangeeDuSommaire[]>([])

/** Une rangée du sommaire : sa clé, son ancre, son titre et son résumé. */
type RangeeAffichee = {
    cle: RangeeDuSommaire
    id: string
    titre: string
    resume: string | null
    /** Le résumé se lit en orange (le point qui touche la conclusion). */
    orange: boolean
    /** Le compte en pastille (« Points à vérifier »), ou `null`. */
    compte: number | null
}

/**
 * Les actions avant d'envoyer de l'étude affichée (contrat v1.2 §6 rangée 4,
 * A1, A7) : la rangée « Points à vérifier » n'existe qu'avec une action.
 */
const getPointsAVerifier = computed(() => pointsDActions(getCurrentEtude.value))

/** Les lignes de « Pourquoi ces puissances » : formule, raccordement, croissance. */
const getLignesDuPourquoi = computed(() =>
    getCurrentEtude.value
        ? lignesDuPourquoi(
              getCurrentEtude.value,
              regleCroissanceDesPostes(getCurrentEtude.value.compteur?.segment)
          )
        : []
)

/** B3 (I14, I37) : pic, postes alignés, marge, trois puces au plus. */
const getPucesDuPourquoi = computed(() =>
    getCurrentEtude.value ? pucesDuPourquoi(getCurrentEtude.value) : []
)

/**
 * Les trois groupes du sommaire (comprendre, vérifier, agir) ; une rangée
 * sans contenu n'est pas écrite, un groupe vide non plus.
 */
const getGroupesDuSommaire = computed<
    { cle: string; rangees: RangeeAffichee[] }[]
>(() => {
    const etude = getCurrentEtude.value
    if (!etude || getModeCadre.value !== "etude") return []
    const ctx = getContexte.value
    const etat = getEtatParcours.value
    const points = getPointsAVerifier.value
    const rangee = (
        cle: RangeeDuSommaire,
        id: string,
        titre: string,
        resume: string | null,
        present: boolean,
        extra: Partial<RangeeAffichee> = {}
    ): RangeeAffichee | null =>
        present
            ? { cle, id, titre, resume, orange: false, compte: null, ...extra }
            : null
    const groupes: { cle: string; rangees: (RangeeAffichee | null)[] }[] = [
        {
            cle: "comprendre",
            rangees: [
                rangee(
                    "composantes",
                    "acc-postes",
                    "Montant par composante",
                    noyauComposantes(etude),
                    getEntetePostes.value.forme !== "absente"
                ),
                rangee(
                    "depassements",
                    "m-t2",
                    getPresentationT2.value.titre,
                    noyauDepassements(ctx),
                    getEnteteT2.value.forme !== "absente"
                ),
                rangee(
                    "pourquoi",
                    "acc-pourquoi",
                    getEntetePourquoi.value.libelle,
                    noyauPourquoi(etude, etat),
                    getEntetePourquoi.value.forme !== "absente"
                ),
            ],
        },
        {
            cle: "verifier",
            rangees: [
                rangee(
                    "points",
                    "acc-points",
                    "Points à vérifier",
                    // Contrat v1.2 §6 4.1 : « 1 point · raccordement à
                    // corriger », en orange ; absente sans action.
                    resumeDesPointsDActions(points),
                    points.length > 0,
                    { orange: true, compte: null }
                ),
                rangee(
                    "hypotheses",
                    "m-cadre",
                    "Hypothèses de l'étude",
                    noyauHypotheses(etude),
                    true
                ),
                rangee(
                    "comparaison",
                    "acc-comparaison",
                    getEnteteComparaison.value.libelle,
                    getEnteteComparaison.value.noyau,
                    getEnteteComparaison.value.forme !== "absente"
                ),
            ],
        },
        {
            cle: "agir",
            rangees: [
                rangee(
                    "rapport",
                    "rapport",
                    "Rapport et demande",
                    noyauRapport(ctx, { resume: Boolean(getResume.value) }),
                    true
                ),
                rangee(
                    "etudes",
                    "acc-etudes",
                    getEnteteEtudes.value.libelle,
                    [
                        getEnteteEtudes.value.noyau,
                        getEnteteEtudes.value.pastille,
                    ]
                        .filter(Boolean)
                        .join(" · ") || null,
                    getEnteteEtudes.value.forme !== "absente"
                ),
            ],
        },
    ]
    return groupes
        .map((g) => ({
            cle: g.cle,
            rangees: g.rangees.filter((r): r is RangeeAffichee => r !== null),
        }))
        .filter((g) => g.rangees.length > 0)
})

/** Ouvre une rangée du sommaire (geste, ancre, lien), sans fermer les autres. */
function ouvrirRangee(cle: RangeeDuSommaire): void {
    if (!ouvertes.value.includes(cle)) ouvertes.value = [...ouvertes.value, cle]
}
/**
 * Un brouillon du cadre non relancé : « 1 modification, non relancée » sur la
 * ligne du cadre (lot B). Le brouillon lui-même vit dans le corps du cadre,
 * jamais démonté par un repli.
 */
const attenteDuCadre = ref<{ etiquette: string; note: string } | null>(null)

/**
 * Parcours guidé, sur un refus (DEV-2311, piste F) : par défaut, seuls les
 * paramètres qui lèvent le refus sont montrés (les remèdes servis) ;
 * « Modifier les paramètres » montre tout. Un nouveau refus revient aux
 * seuls remèdes.
 */
const tousLesParametres = ref(false)
const getRefusAffiche = computed(
    () => getRefus.value ?? (getEchec.value ? getCurrentEtude.value : null)
)
const getRemedes = computed(() =>
    tousLesParametres.value
        ? null
        : remedesDuRefus(
              getRefusAffiche.value ? lireEchec(getRefusAffiche.value) : null
          )
)
watch(
    () => getRefusAffiche.value?._id ?? null,
    () => {
        tousLesParametres.value = false
    }
)

/**
 * Le mode du bloc des paramètres : `entree` sans étude (on montre les
 * paramètres recommandés et on attend le geste), `refus` sur un refus ou une
 * étude rouverte en échec, `etude` sinon.
 */
const getModeCadre = computed<"entree" | "refus" | "etude">(() => {
    if (getRefusAffiche.value) return "refus"
    if (getCurrentEtude.value) return "etude"
    return "entree"
})

/**
 * Q0 : sans étude, le formulaire ouvert sur un compteur ne reste pas ouvert
 * sur le suivant (les ouvertures d'arrivée ne s'appliquent qu'à une étude) ;
 * le brouillon, lui, est démonté avec le formulaire du compteur quitté.
 */
watch(
    () =>
        getModeCadre.value === "entree"
            ? optimTurpeV2Store.currentSourceId
            : null,
    (source) => {
        if (!source) return
        ouvertures.cadre = false
        detailsOuverts.ajuster = false
    }
)

/**
 * Revue témoin du 15/10/2026 (D27, arbitrage 3.7) : à la fin d'un calcul qui
 * aboutit à une étude, le formulaire ouvert pour la relance se referme (sauf
 * si l'état l'exige) et la page défile vers la carte Résultat, que
 * `scroll-mt-36` garde sous la barre collante. Un refus laisse le formulaire
 * ouvert sur son remède.
 */
/**
 * Revue 2 en production (I69, DEV-2404) : après la PREMIÈRE étude d'un
 * compteur, l'écran restait là où était la carte d'attente, le verdict hors
 * champ. Le calcul peut se terminer avant que l'étude ne soit posée (aucune
 * étude affichée avant le lancement) : le retour en haut du résultat reste
 * alors dû, et part dès que l'étude arrive.
 */
let resultatAttendu = false
async function allerAuResultatApresCalcul(): Promise<void> {
    if (getRefus.value || getError.value) {
        resultatAttendu = false
        return
    }
    if (!getCurrentEtude.value) return
    resultatAttendu = false
    ouvertures.cadre = getCadreForce.value
    detailsOuverts.ajuster = false
    await nextTick()
    try {
        document
            .getElementById("m-t1")
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch {
        // Sans défilement doux : le résultat est déjà rendu.
    }
}
watch(
    () => optimTurpeV2Store.isRunning,
    (enCours, etaitEnCours) => {
        if (enCours) resultatAttendu = true
        if (etaitEnCours && !enCours) void allerAuResultatApresCalcul()
    }
)
watch(
    () => getCurrentEtude.value?._id ?? null,
    (id) => {
        if (id && resultatAttendu && !optimTurpeV2Store.isRunning)
            void allerAuResultatApresCalcul()
    }
)
// Un autre compteur : le retour en haut promis au calcul quitté ne vaut plus.
watch(
    () => optimTurpeV2Store.currentSourceId,
    () => {
        if (!optimTurpeV2Store.isRunning) resultatAttendu = false
    }
)

/** « Modifier les paramètres » : l'ajustement s'ouvre, ou tout se montre sur un refus. */
async function onModifierParametres(): Promise<void> {
    if (getModeCadre.value === "refus") {
        tousLesParametres.value = !tousLesParametres.value
        return
    }
    const ouvrir = !(ouvertures.cadre && detailsOuverts.ajuster === true)
    ouvertures.cadre = ouvrir
    detailsOuverts.ajuster = ouvrir
    if (!ouvrir) return
    // Sur un résultat, le formulaire vit dans la rangée « Hypothèses ».
    if (getModeCadre.value === "etude") ouvrirRangee("hypotheses")
    await nextTick()
    try {
        document
            .getElementById("acc-ajuster")
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch {
        // Sans défilement doux : la ligne est déjà ouverte.
    }
}

/**
 * « Modifier les hypothèses » de la carte (cas C sans période proposée) :
 * ouvre toujours la rangée et son formulaire, jamais ne les referme.
 */
/**
 * « Préparer la demande » (D3 a, D16 a) : la rangée « Rapport et demande »
 * s'ouvre sur l'aperçu du courriel (ancre #rapport).
 */
async function onPreparerDemande(): Promise<void> {
    courrielOuvert.value = true
    ouvrirRangee("rapport")
    await nextTick()
    try {
        document
            .getElementById("courriel")
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch {
        // Un navigateur sans défilement doux : la rangée est déjà ouverte.
    }
}

/**
 * « Voir le résumé » (D16 a, A2) : la rangée s'ouvre sur « Résumé pour votre
 * client », mis en forme, avant « Copier le résumé ».
 */
async function onVoirResume(): Promise<void> {
    ouvertures.remise = true
    ouvrirRangee("rapport")
    await nextTick()
    try {
        document
            .getElementById("acc-remise")
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch {
        // Un navigateur sans défilement doux : la rangée est déjà ouverte.
    }
}

async function onModifierHypotheses(): Promise<void> {
    ouvertures.cadre = false
    detailsOuverts.ajuster = false
    await onModifierParametres()
}

/**
 * La personne et la société, pour la clé nominative de la mémoire (D19).
 * Sans elles, rien n'est gardé.
 */
function identiteDuLecteur(): { userId: string; companyId: string } | null {
    try {
        const auth = useAuthStore()
        return { userId: auth.userId, companyId: auth.companyId }
    } catch {
        return null
    }
}
const identite = identiteDuLecteur()
const getCleMemoire = computed(() =>
    cleDeMemoire(identite?.userId, identite?.companyId)
)
function stockage(): Storage | null {
    try {
        return window.localStorage
    } catch {
        return null
    }
}
let memoire: MemoireOuvertures = memoireVide()

/**
 * La règle de parcours par état (lot C, cible 11.1) : statut de chaque
 * section, section d'ouverture, action principale. Ce que l'état ouvre
 * d'office passe avant le geste, la mémoire et le premier jour.
 */
const getRegle = computed(() => regleDeParcours(getContexte.value))
const getForcesDeLaRegle = computed(() => forcesDeLaRegle(getRegle.value))
const getForces = computed<Partial<Record<TempsRepliable, boolean>>>(() => ({
    t2: getForcesDeLaRegle.value.t2,
    t3: getForcesDeLaRegle.value.t3,
    t4: getForcesDeLaRegle.value.t4,
}))
/**
 * Un refus avec levier ouvre le cadre sur son remède ; un refus sans levier
 * laisse le cadre replié : la cause et l'aide se lisent au temps 1.
 */
const getCadreForce = computed(
    () => getForcesDeLaRegle.value.cadre && !getIsLoading.value
)

/** La clé de l'objet étudié : le compteur, ou la fixture en mode mock. */
const getCleObjet = computed(() =>
    isMock.value ? selectedEtudeId.value : optimTurpeV2Store.currentSourceId
)
let objetInitialise: string | null = null

/**
 * À l'arrivée sur un compteur : état, puis geste (`?ouvrir=`), puis mémoire,
 * puis premier jour. Sur une étude neuve du même compteur, seul l'état
 * rouvre ce qu'il exige : les temps que le lecteur a ouverts ou repliés
 * restent. Le formulaire des hypothèses fait exception (arbitrage 3.7 du
 * 15/10/2026, D27) : après une relance réussie, il se referme et la page
 * ramène le lecteur à la carte Résultat (`allerAuResultatApresCalcul`).
 */
function appliquerOuvertures(): void {
    const objet = getCleObjet.value
    if (!objet || (!getCurrentEtude.value && !getRefus.value)) return
    if (objet !== objetInitialise) {
        objetInitialise = objet
        memoire = lireMemoire(stockage(), getCleMemoire.value)
        const ouvrir = lireParametreOuvrir(route.query.ouvrir)
        const initiales = ouverturesInitiales({
            forces: getForces.value,
            ouvrir,
            memoire,
        })
        ouvertures.t2 = initiales.t2
        ouvertures.t3 = initiales.t3
        ouvertures.t4 = initiales.t4
        ouvertures.remise = initiales.remise
        ouvertures.demarche = initiales.demarche
        ouvertures.cadre = initiales.tout || getCadreForce.value
        for (const cle of Object.keys(detailsOuverts))
            delete detailsOuverts[cle]
        if (initiales.tout) {
            for (const id of DETAILS) detailsOuverts[id] = true
        }
        // Maquette finale : tout replié, sauf l'historique d'une étude qui
        // n'est pas la recommandation, la rangée d'une ancre, ou `?ouvrir=`.
        ouvertes.value = rangeesOuvertesInitiales({
            ouvrir,
            nonRecommandee: getEtatParcours.value === "scenario_saisi",
            ancre: route.hash,
        })
        // Un état qui exige les hypothèses (montant non chiffré sans période
        // plus complète) ouvre leur rangée.
        if (getCadreForce.value && getModeCadre.value === "etude")
            ouvrirRangee("hypotheses")
        return
    }
    // Une étude neuve du même compteur : ce que le lecteur a ouvert reste ;
    // une étude qui n'est pas la recommandation ouvre l'historique.
    if (getEtatParcours.value === "scenario_saisi") ouvrirRangee("etudes")
    if (getForces.value.t2) ouvertures.t2 = true
    if (getCadreForce.value) ouvertures.cadre = true
    if (getCadreForce.value && getModeCadre.value === "etude")
        ouvrirRangee("hypotheses")
}

/** Les lignes de rang 2 de la page (maquette allégée), repliées au repos. */
const DETAILS = [
    "contrat",
    "ajuster",
    "avances",
    "postes",
    "comparaison",
    "pourquoi",
    "scenarios",
    "limites",
    "etudes",
] as const

/**
 * Un geste du lecteur sur un temps, ou sur une section d'accompagnement : il
 * est retenu pour la prochaine visite.
 */
function onBasculerTemps(
    temps: TempsRepliable | SectionAccompagnement,
    ouvert: boolean
): void {
    ouvertures[temps] = ouvert
    memoire = { ...memoire, [temps]: ouvert }
    ecrireMemoire(stockage(), getCleMemoire.value, memoire)
}

function onBasculerDetail(id: string, ouvert: boolean): void {
    detailsOuverts[id] = ouvert
}

/** Les sections repliables présentes dans cet état, et leur ouverture. */
const getRepliables = computed<{ id: string; ouvert: boolean }[]>(() => {
    const liste: { id: string; ouvert: boolean }[] = [
        { id: "cadre", ouvert: ouvertures.cadre },
    ]
    const temps: [TempsRepliable, Entete][] = [
        ["t2", getEnteteT2.value],
        ["t3", getEnteteT3.value],
        ["t4", getEnteteT4.value],
    ]
    for (const [id, e] of temps) {
        if (e.forme === "repliee") liste.push({ id, ouvert: ouvertures[id] })
    }
    if (getEnteteResume.value.forme === "repliee") {
        liste.push({ id: "remise", ouvert: ouvertures.remise })
    }
    const etude = getModeCadre.value === "etude"
    const details: [string, boolean][] = [
        ["contrat", etude && getEnteteCalcul.value.forme === "repliee"],
        ["ajuster", etude],
        ["postes", getEntetePostes.value.forme === "repliee"],
        ["comparaison", getEnteteComparaison.value.forme === "repliee"],
        ["pourquoi", getEntetePourquoi.value.forme === "repliee"],
        ["scenarios", etude && optimTurpeV2Store.getArbitragePertinent],
        ["limites", etude && getAvecLimites.value],
        ["etudes", getEnteteEtudes.value.forme === "repliee"],
    ]
    for (const [id, present] of details) {
        if (present) liste.push({ id, ouvert: detailsOuverts[id] === true })
    }
    return liste
})
const getToutOuvert = computed(() => getRepliables.value.every((r) => r.ouvert))

/**
 * « Tout déplier » ouvre tout, cadre compris ; « Tout replier » laisse ouvert
 * ce que l'état a ouvert. La mémoire garde les cinq réponses ; `muet` (le
 * lien `?ouvrir=tout`) ne la touche pas.
 */
function onTout(ouvrir: boolean): void {
    ouvertures.cadre = ouvrir || getCadreForce.value
    for (const t of ["t2", "t3", "t4"] as const) {
        ouvertures[t] = ouvrir || getForces.value[t] === true
    }
    ouvertures.remise = ouvrir
    ouvertures.demarche = ouvrir
    for (const id of DETAILS) detailsOuverts[id] = ouvrir
    memoire = { ...memoire }
    for (const section of SECTIONS_MEMORISEES) memoire[section] = ouvrir
    ecrireMemoire(stockage(), getCleMemoire.value, memoire)
}

/** Le fil : une ancre qui conduit à une section repliée la déroule. */
const getEtapesDuFil = computed<EtapeDuFil[]>(() =>
    (
        [
            {
                cible: "m-cadre",
                numero: null,
                libelle: t("parcours.filCadre"),
                estompee: false,
            },
            {
                cible: "m-t1",
                numero: null,
                libelle: t("parcours.filT1"),
                estompee: false,
            },
            // A8 : une section retirée (sans contenu) n'est plus dans le fil.
            getEnteteT2.value.forme === "absente"
                ? null
                : {
                      cible: "m-t2",
                      numero: null,
                      libelle: getPresentationT2.value.fil,
                      estompee: getEnteteT2.value.forme !== "repliee",
                  },
            getEnteteT3.value.forme === "absente"
                ? null
                : {
                      cible: "rapport",
                      numero: null,
                      libelle: t("parcours.filT3"),
                      estompee: getEnteteT3.value.forme !== "repliee",
                  },
            getEnteteEtudes.value.forme === "absente"
                ? null
                : {
                      cible: "acc-etudes",
                      numero: null,
                      libelle: t("parcours.filEtudes"),
                      estompee: false,
                  },
        ] as (EtapeDuFil | null)[]
    ).filter((e): e is EtapeDuFil => e !== null)
)

async function onAller(cible: string): Promise<void> {
    const temps = ({ "m-t2": "t2", "m-t3": "t3", "m-t4": "t4" } as const)[
        cible as "m-t2" | "m-t3" | "m-t4"
    ]
    if (temps) ouvertures[temps] = true
    // Sur un résultat, l'ancre ouvre sa rangée du sommaire.
    const rangee = rangeeDeLAncre(cible)
    if (rangee) ouvrirRangee(rangee)
    await nextTick()
    try {
        document
            .getElementById(cible)
            ?.scrollIntoView({ behavior: "smooth", block: "start" })
    } catch {
        // Un navigateur sans défilement doux : l'ancre est déjà ouverte.
    }
}

watch(
    () => [
        getCleObjet.value,
        getCurrentEtude.value?._id,
        getRefus.value?._id,
        getIsLoading.value,
    ],
    () => appliquerOuvertures(),
    { immediate: true }
)

/**
 * Contrat v1.2 §7.4 : à chaque étude, le courriel se replie derrière
 * « Préparer la demande » ; sans démarche, le résumé pour le client se
 * présente déplié d'office.
 */
watch(
    () => [getCurrentEtude.value?._id, getAvecDemarche.value],
    () => {
        courrielOuvert.value = false
        if (getCarte.value && !getAvecDemarche.value) ouvertures.remise = true
    },
    { immediate: true }
)

// ================================
// RETOURS (D31, maquette mq-retours, partie 8 option B)
// ================================
/**
 * L'encart de retours, sous le résultat (ou sous le refus), ne s'affiche
 * jamais de lui-même : seul le lien « Donner votre avis sur cette étude »
 * l'ouvre (décision du 06/10/2026, « pour ne pas polluer »). Jamais avant une
 * issue, jamais en fenêtre. Le journal, dans le navigateur et par utilisateur,
 * ne garde que les avis envoyés : rouvert sur une étude déjà commentée,
 * l'encart remercie au lieu de redemander. Illisible ou indisponible, il vaut
 * un journal vide.
 */
const cleRetours = cleJournalRetours(useAuthStore().userId ?? "")
function lireJournalStocke(): JournalRetours {
    try {
        return lireJournal(window.localStorage.getItem(cleRetours))
    } catch {
        return journalVide()
    }
}
function ecrireJournal(journal: JournalRetours): void {
    journalRetours.value = journal
    try {
        window.localStorage.setItem(cleRetours, JSON.stringify(journal))
    } catch {
        // Stockage indisponible : l'avis pourra être redemandé, rien de plus.
    }
}
const journalRetours = ref<JournalRetours>(lireJournalStocke())
const retours = reactive({ visible: false, ouvert: false })

/** L'issue affichée sur laquelle porte l'avis : un refus, ou un résultat. */
const getIssueRetours = computed<{
    etudeId: string
    variante: VarianteRetours
    etat: string | null
} | null>(() => {
    if (getIsLoading.value) return null
    const refus = getRefus.value
    if (refus?._id) {
        return {
            etudeId: refus._id,
            variante: "refus",
            etat: lireEchec(refus)?.code ?? null,
        }
    }
    const etude = getCurrentEtude.value
    if (!etude?._id) return null
    const echec = getEchec.value
    if (echec) {
        return { etudeId: etude._id, variante: "refus", etat: echec.code }
    }
    return {
        etudeId: etude._id,
        variante: "resultat",
        etat: lireChiffre(etude).etat ?? null,
    }
})
const getEnvoiRetours = computed(() => {
    const id = getIssueRetours.value?.etudeId
    return id ? (journalRetours.value.envois[id] ?? null) : null
})

// Une autre étude affichée : l'encart se retire jusqu'au prochain clic.
watch(
    () => getIssueRetours.value?.etudeId ?? null,
    () => {
        retours.visible = false
        retours.ouvert = false
    }
)

/** Le lien du pied de page : l'encart, ouvert, sous les yeux. */
async function onDonnerAvis(): Promise<void> {
    if (!getIssueRetours.value) return
    retours.visible = true
    retours.ouvert = true
    await nextTick()
    try {
        document
            .getElementById("acc-retours")
            ?.scrollIntoView({ behavior: "smooth", block: "center" })
    } catch {
        // Sans défilement doux : l'encart est déjà ouvert.
    }
}

/** Un avis parti : le journal le garde, la mesure reçoit les réponses fermées. */
function onAvisEnvoye(reponses: ReponsesRetours): void {
    const issue = getIssueRetours.value
    if (!issue) return
    ecrireJournal(noterEnvoi(journalRetours.value, issue.etudeId, new Date()))
    const mesure = reponsesMesurees(issue.variante, reponses)
    if (!mesure) return
    track({
        event: "Optim V2 Feedback Sent",
        payload: {
            building_id: buildingId(),
            analyse_id: issue.etudeId,
            source_segment: segmentPourMesure(),
            ...mesure,
        },
    })
}

// ================================
// INTERACTIONS
// ================================
/**
 * Instrumentation (F9.5, M1). Les propriétés utiles à la mesure sont celles que le
 * MOTEUR sert : l'état, le niveau de qualité, le fait que le montant soit servi.
 * Un événement de résultat n'est émis qu'une fois par étude affichée — sans cette
 * garde, chaque re-rendu de la page en émettrait un nouveau et la mesure compterait
 * des résultats qui n'ont pas été recalculés.
 */
const etudesMesurees = new Set<string>()

function mesurerLancement(
    sourceId: string,
    origine: "ouverture" | "relance" | "meilleure_periode",
    overrides?: OptimTurpeV2RunBody
): void {
    track({
        event: "Optim V2 Study Launched",
        payload: {
            building_id: buildingId(),
            source_id: sourceId,
            source_segment: segmentPourMesure(),
            origine,
            surcharges: cheminsSurcharges(overrides),
            // `null` : retour à l'optimisation depuis « Vos puissances ».
            mode: overrides?.optimisation?.mode ?? undefined,
        },
    })
    for (const champ of cheminsSurcharges(overrides) ?? []) {
        track({
            event: "Optim V2 Input Overridden",
            payload: {
                building_id: buildingId(),
                source_id: sourceId,
                champ,
            },
        })
    }
}

/**
 * Ouvre un compteur (DEV-2156) : son étude déjà calculée s'affiche sans calcul ;
 * s'il n'en a aucune, UN lancement part (une paire d'études, sans et avec
 * dépassement). Le lancement n'est mesuré que s'il a eu lieu.
 */
/**
 * Les ouvertures de compteur en vol (liste des études, puis l'étude à
 * rouvrir) : tant qu'il en reste une, « Lancer l'étude » ne s'affiche pas
 * (clignotement mesuré en prod le 20/10/2026, 457 à 1 259 ms). Un compteur,
 * pas un drapeau : un déclencheur surnuméraire rend la main tout de suite.
 */
const ouverturesEnVol = ref(0)
/** Au-delà, l'ouverture n'attend plus la relecture de l'état de la collecte. */
const ATTENTE_MAX_RELECTURE_MS = 3000
const getEnOuverture = computed(() => ouverturesEnVol.value > 0)

async function ouvrirCompteur(sourceId: string): Promise<void> {
    // Reco (p) : l'état de la collecte se relit à la sélection du compteur
    // (`data_retrieval.status`, même route que la carte compteur) ; la carte
    // d'attente ne s'appuie jamais sur un état gardé depuis l'arrivée. La
    // relecture compte dans l'ouverture : sans cela, « Lancer l'étude »
    // pouvait s'afficher puis céder la place à la carte. Son attente est
    // bornée : une relecture qui traîne ne cache pas le bouton.
    const relecture = sourceStore
        .fetchSourceById({ _id_source: sourceId, silent: true })
        .catch(() => {})
    ouverturesEnVol.value += 1
    try {
        await Promise.all([
            ouvrirLeCompteur(sourceId),
            Promise.race([
                relecture,
                new Promise<void>((r) =>
                    setTimeout(r, ATTENTE_MAX_RELECTURE_MS)
                ),
            ]),
        ])
    } finally {
        ouverturesEnVol.value -= 1
    }
}

async function ouvrirLeCompteur(sourceId: string): Promise<void> {
    // Parcours guidé (07/10/2026) : un compteur sans étude s'ouvre sur les
    // paramètres recommandés ; l'étude ne part que sur « Lancer l'étude
    // recommandée ».
    await optimTurpeV2Store.ouvrirSource(
        sourceId,
        getRunOverrides(),
        { segment: segmentDe(sourceId) },
        { lancerSiAucune: false }
    )
}

/**
 * « Lancer l'étude recommandée » : le premier lancement d'un compteur, sur
 * les paramètres recommandés (période du moteur, prudence équilibrée posée
 * par le magasin, contrat lu chez Enedis). Le corps est celui d'un lancement
 * sans réglage : aucune route ni aucun corps neuf.
 */
async function onLancerRecommandee(): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    if (getConsentementEnAttente.value) return
    if (getDonneesEnAttente.value) return
    const sourceId = optimTurpeV2Store.currentSourceId
    mesurerLancement(sourceId, "ouverture")
    await relancer(sourceId, {}, { fenetreParDefaut: true })
}

/**
 * Q0 (revue du 08/10/2026) : le premier lancement depuis « Ajuster l'étude »,
 * avant toute étude. Le même lancement que « Lancer l'étude recommandée »
 * (paire d'études, mesure « ouverture », aucun rejeu possible), avec les
 * surcharges saisies.
 */
async function onLancerAvecParametres(
    overrides: OptimTurpeV2RunBody
): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    if (getConsentementEnAttente.value) return
    if (donneesEnAttente(saisiesDuLancement(overrides))) return
    const sourceId = optimTurpeV2Store.currentSourceId
    mesurerLancement(sourceId, "ouverture", overrides)
    await relancer(sourceId, overrides, { fenetreParDefaut: true })
}

/** Le bouton du formulaire : premier lancement sans étude, relance sinon. */
async function onRelancerDuCadre(
    overrides: OptimTurpeV2RunBody
): Promise<void> {
    if (getModeCadre.value === "entree") {
        await onLancerAvecParametres(overrides)
        return
    }
    await onRelancer(overrides)
}

/** Sélection d'un compteur : charge la fixture (mock) ou ouvre le compteur réel. */
async function onSelectMeter(value: string): Promise<void> {
    if (isMock.value) {
        selectedEtudeId.value = value
        optimTurpeV2Store.setMockEtude(value)
        return
    }
    if (!value || value === optimTurpeV2Store.currentSourceId) return
    sourceOuverteParLaVisite = value
    await router.replace({ query: { ...route.query, idsource: value } })
    await ouvrirCompteur(value)
}

/**
 * « Relancer l'étude sur les données du jour » / « Lancer l'étude », sur le
 * geste du client seulement. La page ne fabrique AUCUNE fenêtre (DEV-2178) :
 * la période est celle du moteur, 365 jours finissant à la dernière donnée
 * détenue. Un lancement neuf n'envoie donc aucune date ; un rejeu envoie des
 * bornes nulles, sans quoi il reprendrait la période de l'étude rejouée.
 */
async function onLancerDuJour(): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    const sourceId = optimTurpeV2Store.currentSourceId
    mesurerLancement(sourceId, "relance")
    await relancer(sourceId, {}, { fenetreParDefaut: true })
}

/**
 * « Relancer en mode recommandé », sur un refus (constat du 07/10) : la relance
 * repart des paramètres recommandés, quel que soit l'état du cadre. Le corps
 * est celui de « Lancer l'étude recommandée » : aucune période (celle du
 * moteur, 365 jours finissant au dernier jour mesuré), aucun comptage, aucune
 * structure ni aucun compteur (le contrat lu chez Enedis), la prudence
 * « Équilibré » posée explicitement pour que la prudence choisie pendant la
 * visite ne revienne pas. Un lancement NEUF, jamais un rejeu : le rejeu
 * reprendrait les entrées de l'étude rejouée.
 */
async function onRelancerRecommande(): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    const sourceId = optimTurpeV2Store.currentSourceId
    const corps: OptimTurpeV2RunBody = {
        optimisation: { marge_securite_pct: MARGE_FRACTION_PAR_DEFAUT },
    }
    mesurerLancement(sourceId, "relance", corps)
    await relancer(sourceId, corps, { recommande: true })
}

/**
 * Le chemin UNIQUE d'une relance (DEV-2150) : les études affichées se
 * REJOUENT quand elles le peuvent (étude neuve chaînée, l'ancienne marquée
 * « remplacée le … », origine `rejeu`), sinon un lancement neuf part. Un
 * scénario saisi (`price_only`) reste un lancement : le rejeu le rendrait
 * courant à la place de l'étude qu'il compare.
 */
async function relancer(
    sourceId: string,
    corps: OptimTurpeV2RunBody,
    options: { fenetreParDefaut?: boolean; recommande?: boolean } = {}
): Promise<void> {
    // Retours du 23/10/2026 : tout lancement efface l'avis d'une période
    // proposée bloquée, qu'il aboutisse ou qu'il soit refusé.
    avisPeriodeProposee.value = null
    const ids = options.recommande
        ? []
        : optimTurpeV2Store.idsAffichesRejouables
    if (ids.length > 0 && corps.optimisation?.mode !== "price_only") {
        await optimTurpeV2Store.rejouerLancement(
            ids,
            surchargesDeRejeu(corps, options)
        )
        return
    }
    try {
        await optimTurpeV2Store.runArbitrage(
            sourceId,
            { ...getRunOverrides(), ...corps },
            { segment: segmentDe(sourceId) }
        )
    } catch {
        // L'échec est porté par `runError` ou par le refus (DEV-2163) : la page
        // le dit, et les réglages restent ouverts sur la période saisie.
    }
    await optimTurpeV2Store.chargerHistorique(sourceId)
}

/**
 * Une période proposée après un refus (DEV-2164, cas 1 et 2). Retours du
 * 23/10/2026 : c'est le chemin même du défaut (la période servie par le refus,
 * `echec.meilleurePeriode`) ; elle passe la même garde que le panneau
 * « Affiner l'étude » avant de partir.
 */
async function onEtudierPeriode(periode: PeriodeProposee): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    avisPeriodeProposee.value = refusDeLaPeriodeProposee(
        periode,
        optimTurpeV2Store.currentSourceId
    )
    if (avisPeriodeProposee.value) return
    await onRelancer({
        date_from: periode.date_from,
        date_to: periode.date_to,
    })
}

/**
 * Overrides envoyés au moteur v2 à chaque run. `_id_building` (params de route) est
 * REQUIS côté courtage pour persister l'AnalyseV2 tenant-scopée (sinon 422).
 */
function getRunOverrides(): { _id_building: string } {
    return { _id_building: String(route.params._id_building ?? "") }
}

/** Premier compteur éligible (fallback de sélection). */
function resolveFirstEligibleSourceId(): string {
    return getEligibleMeters.value[0]?._id_source ?? ""
}

/**
 * Le compteur que CETTE visite de la page a ouvert, ou `null` (DEV-2311,
 * revue du 08/10, R4).
 *
 * Le magasin survit à la navigation : quitter la page pendant le calcul
 * l'abandonne (rien n'est écrit au retour du POST), et le magasin garde le
 * compteur sans étude. Au retour, l'ancienne garde « compteur déjà aligné »
 * comparait au compteur DU MAGASIN et ne rouvrait rien : le client revoyait
 * l'état d'entrée, « j'ai plus rien ». Chaque visite rouvre donc une fois son
 * compteur (l'étude terminée s'affiche, un calcul encore en file reprend son
 * suivi) ; seules les re-synchronisations suivantes de la même visite se
 * taisent quand le compteur est déjà aligné.
 */
let sourceOuverteParLaVisite: string | null = null

/** Les compteurs étudiés du bâtiment, lus une fois par visite. */
let sourcesEtudiees: Promise<string[] | null> | null = null

/**
 * La dernière étude du compte quand elle porte sur un AUTRE bâtiment et que
 * celui-ci n'en a aucune (R4) : l'état d'entrée le dit en une ligne, avec le
 * lien vers ce bâtiment. `null` : rien à dire.
 */
const etudeAilleurs = ref<{ buildingId: string; nom: string } | null>(null)

/** Le lien vers la page Optim TURPE du bâtiment de la dernière étude. */
const getLienEtudeAilleurs = computed(() =>
    etudeAilleurs.value
        ? `/building/${etudeAilleurs.value.buildingId}/optimisation-contractuelle-v2`
        : null
)

/**
 * Le bâtiment n'a aucune étude : on cherche, une fois par visite, si la
 * dernière étude du compte est ailleurs (R4). Ne lit rien si le bâtiment a
 * déjà des études.
 */
async function signalerEtudeAilleurs(): Promise<void> {
    if (!sourcesEtudiees) return
    const batiment = buildingId()
    const ids = await sourcesEtudiees
    // Liste illisible : on ne sait pas si le bâtiment a des études.
    if (ids === null || ids.length > 0) return
    const ailleurs = await optimTurpeV2Store.derniereEtudeAilleurs(batiment)
    // Le lecteur a changé de bâtiment pendant la lecture.
    if (batiment !== buildingId()) return
    etudeAilleurs.value = ailleurs
}

/**
 * Le compteur éligible le plus récemment étudié du bâtiment, ou `""` (R4) :
 * sans `?idsource=`, c'est lui que la page rouvre, avant de retomber sur le
 * premier compteur éligible.
 */
async function resolveDernierEtudieSourceId(): Promise<string> {
    sourcesEtudiees ??=
        optimTurpeV2Store.sourcesEtudieesDuBatiment(buildingId())
    const ids = await sourcesEtudiees
    return (
        ids?.find((id) =>
            getEligibleMeters.value.some((m) => m._id_source === id)
        ) ?? ""
    )
}

/**
 * Aligne la source sur `?idsource=` (deep-link), sinon sur le dernier compteur
 * étudié du bâtiment, sinon sur le 1er éligible, puis ouvre le compteur. La
 * première ouverture de la visite a toujours lieu (R4) ; ensuite, no-op si
 * déjà alignée (évite les re-runs sur re-éval des compteurs).
 */
async function syncSourceFromRoute(): Promise<void> {
    const querySourceId =
        typeof route.query.idsource === "string" ? route.query.idsource : ""
    const isQueryEligible =
        querySourceId !== "" &&
        getEligibleMeters.value.some((m) => m._id_source === querySourceId)
    let sourceId = isQueryEligible ? querySourceId : ""
    if (!sourceId && getEligibleMeters.value.length > 0) {
        const premiereLecture = sourcesEtudiees === null
        sourceId =
            (await resolveDernierEtudieSourceId()) ||
            resolveFirstEligibleSourceId()
        if (premiereLecture) void signalerEtudeAilleurs()
    }
    if (!sourceId || sourceId === sourceOuverteParLaVisite) return
    if (
        sourceOuverteParLaVisite !== null &&
        sourceId === optimTurpeV2Store.currentSourceId
    )
        return
    sourceOuverteParLaVisite = sourceId
    if (querySourceId !== sourceId) {
        await router.replace({ query: { ...route.query, idsource: sourceId } })
    }
    await ouvrirCompteur(sourceId)
}

/**
 * Relance l'étude sur la période recommandée (DEV-762). On n'impose rien : la conclusion
 * propose une fenêtre plus complète, l'utilisateur clique s'il le veut. No-op en mock.
 */
async function onRelancerPeriode(periode: {
    date_from: string
    date_to: string
}): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    const sourceId = optimTurpeV2Store.currentSourceId
    // Retours du 23/10/2026 : même garde que le panneau « Affiner l'étude ».
    // Une période que le moteur refuserait à coup sûr pour postes absents ne
    // part pas ; la page le dit, avec la phrase du panneau.
    avisPeriodeProposee.value = refusDeLaPeriodeProposee(periode, sourceId)
    if (avisPeriodeProposee.value) return
    mesurerLancement(sourceId, "meilleure_periode")
    // L'étude calculée apparaît dans la liste du compteur (revue C8) : `relancer`
    // recharge l'historique après chaque lancement.
    await relancer(sourceId, {
        date_from: periode.date_from,
        date_to: periode.date_to,
    })
}

/**
 * La phrase du refus certain d'une période proposée, ou `null` (retours du
 * 23/10/2026). Même règle que le panneau « Affiner l'étude » : segment
 * normalisé (un Linky P3 est un C4) et conduite de l'étude qui partira ; hors
 * « prendre la main », où le moteur ne refuse pas.
 */
function refusDeLaPeriodeProposee(
    periode: PeriodeProposee,
    sourceId: string
): string | null {
    const etude = getEtudeReglages.value
    const segment = segmentNormalise(
        etude?.compteur?.segment ?? segmentDe(sourceId)
    )
    const absents = postesAbsentsRefuses(
        periode.date_from,
        periode.date_to,
        segment,
        modeDuRelancement()
    )
    return absents
        ? t("periodeProposee.refusPostes", textesDesPostes(absents.postes))
        : null
}

/**
 * La conduite de l'étude qui partira, comme `relancer` la choisit : le rejeu
 * des études affichées garde la leur ; sans étude à rejouer (un refus à
 * l'écran, ou rien d'affiché), une étude neuve part en mode recommandé, où la
 * garde s'applique (`undefined`).
 */
function modeDuRelancement(): string | undefined {
    if (optimTurpeV2Store.idsAffichesRejouables.length === 0) return undefined
    return getCurrentEtude.value?.inputs?.optimisation?.mode ?? undefined
}

/**
 * Ce que le bouton de la période proposée a à dire quand il ne relance pas.
 * Il s'efface à chaque lancement (`relancer`, ou tout calcul en cours), au
 * changement d'étude, de refus ou de compteur ; un nouveau clic sur une
 * période le recompose.
 */
const avisPeriodeProposee = ref<string | null>(null)
watch(
    () => [
        optimTurpeV2Store.isRunning,
        getCurrentEtude.value?._id ?? null,
        getRefus.value,
        optimTurpeV2Store.currentSourceId,
    ],
    () => {
        avisPeriodeProposee.value = null
    }
)

/**
 * Relance avec les overrides du pilotage (Z2 « Affiner l'étude », DEV-783) — période, marge,
 * corrections d'entrée. `getRunOverrides()` (le `_id_building` requis) reste toujours présent.
 */
async function onRelancer(overrides: OptimTurpeV2RunBody): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    const sourceId = optimTurpeV2Store.currentSourceId
    mesurerLancement(sourceId, "relance", overrides)
    // Le scénario saisi (A6) : chiffré tel quel, mesuré à part, et présenté comme
    // un scénario — jamais substitué en silence à l'étude courante (Q-CIBLE-7).
    if (overrides.optimisation?.mode === "price_only") {
        const cible = overrides.optimisation.cible
        const aFta = Boolean(cible?.formule_tarifaire_acheminement?.fta)
        const aPuissance = Boolean(cible?.puissance_souscrite_soutirage)
        track({
            event: "Optim V2 Scenario Priced",
            payload: {
                building_id: buildingId(),
                source_id: sourceId,
                source_segment: segmentPourMesure(),
                cible:
                    aFta && aPuissance
                        ? "fta_et_denivele"
                        : aFta
                          ? "fta"
                          : aPuissance
                            ? "denivele"
                            : "inconnue",
            },
        })
    }
    // Après CHAQUE run, l'historique du compteur est rechargé (revue C8) :
    // `relancer` le fait, rejeu comme lancement neuf.
    await relancer(sourceId, overrides)
}

/**
 * « Chiffrer d'autres puissances » (Q27) : le cadre s'ouvre sur la saisie
 * des puissances à chiffrer, objectif « le coût des puissances de mon choix ».
 */
async function onChiffrerAutres(): Promise<void> {
    await onCompleter("chiffrer")
}

/**
 * « Voir la recommandation » (Q27, R24) depuis une étude saisie : rouvre la
 * dernière recommandation aboutie du compteur, sans recalcul, ou la rétablit
 * quand un refus l'a marquée remplacée (DEV-2439). Tant que le compteur a une
 * étude aboutie du moteur actuel, AUCUNE étude neuve ne part : sans
 * recommandation aboutie, la page le dit. Sans aucune étude aboutie, elle
 * lance l'étude avec les hypothèses par défaut.
 */
async function onVoirRecommandation(): Promise<void> {
    // Un geste du cycle déjà en cours (double clic) ne repart pas.
    if (optimTurpeV2Store.geste !== null) return
    avisRecommandation.value = null
    const historique = optimTurpeV2Store.historique
    const aRendre = recommandationARendre(historique)
    if (aRendre) {
        await rendreRecommandation(aRendre)
        // Un rétablissement refusé se dit ici : la liste qui le dit
        // d'habitude n'est pas forcément ouverte. Seul le rétablissement
        // remet l'erreur du geste à zéro en partant : après une simple
        // ouverture, elle serait celle d'un geste plus ancien.
        if (aRendre.geste === "retablir" && optimTurpeV2Store.gesteErreur)
            avisRecommandation.value = optimTurpeV2Store.gesteErreur
        return
    }
    if (aUneEtudeAboutie(historique)) {
        avisRecommandation.value = t("recommandationPrecedente.aucune")
        return
    }
    await onRelancerRecommande()
}

/** Ce que « Voir la recommandation » a à dire quand il ne rouvre rien. */
const avisRecommandation = ref<string | null>(null)
watch(
    () => getCurrentEtude.value?._id ?? null,
    () => {
        avisRecommandation.value = null
    }
)

/** Rend une recommandation : la relire, ou la rétablir si elle est remplacée. */
async function rendreRecommandation(
    aRendre: RecommandationARendre
): Promise<void> {
    if (aRendre.geste === "retablir")
        await onRetablirEtude(aRendre.lancement.ids)
    else await onOuvrirEtude(aRendre.lancement.ids)
}

/**
 * Sous un refus (DEV-2439) : la recommandation d'avant, à rendre en un clic.
 * Une relance refusée l'avait marquée remplacée jusqu'au correctif du
 * moteur ; ces études existent en base, et se rétablissent.
 */
const getRecommandationPrecedente = computed(() => {
    if (!getRefusAffiche.value) return null
    const aRendre = recommandationARendre(optimTurpeV2Store.historique)
    if (!aRendre) return null
    return {
        ...aRendre,
        date: formatDateHeureParis(aRendre.lancement.date),
    }
})

/** Ouvre un lancement de l'historique, sans rien recalculer (A12). */
async function onOuvrirEtude(ids: string[]): Promise<void> {
    await optimTurpeV2Store.ouvrirLancement(ids)
}

/** Supprime un lancement (suppression logique), après confirmation dans la page. */
async function onSupprimerEtude(ids: string[]): Promise<void> {
    await optimTurpeV2Store.supprimerLancement(ids)
}

/**
 * Le compteur a-t-il une étude en attente de calcul depuis plus de quinze
 * minutes (DEV-2412) ? Elle ne s'ouvre pas et ne bloque pas un lancement,
 * mais la page ne revient pas pour autant à un état d'entrée muet : elle
 * montre « Études de ce compteur », avec cette étude datée et « Relancer ».
 */
const getAttenteAbandonnee = computed(
    () => lancementsEnAttenteAbandonnes(optimTurpeV2Store.historique).length > 0
)

/**
 * « Relancer » une étude restée en attente (DEV-2412) : un lancement NEUF
 * sur ses réglages. Elle ne se rejoue pas, le moteur refuse le rejeu d'une
 * étude non terminée.
 */
async function onRelancerEnAttente(ids: string[]): Promise<void> {
    if (isMock.value || !optimTurpeV2Store.currentSourceId) return
    if (getConsentementEnAttente.value) return
    if (optimTurpeV2Store.isRunning) return
    const lancements = grouperParLancement(optimTurpeV2Store.historique)
    const lancement = lancements.find((l) =>
        l.ids.some((id) => ids.includes(id))
    )
    if (!lancement) return
    // Finitions après relecture : un lancement déjà relancé sur les mêmes
    // réglages ne se relance plus ; chaque clic en créait un de plus.
    if (relanceDuLancement(lancement, lancements)) return
    const etude = lancement.principale
    const sourceId = optimTurpeV2Store.currentSourceId
    // Reco (p), juge : pas de garde ici, le refus nommé du moteur reste servi
    // pour la relance (Q28) ; la carte d'attente ne vaut qu'avant le lancement.
    const corps = corpsDeRelance(etude)
    mesurerLancement(sourceId, "relance", corps)
    await relancer(sourceId, corps, { recommande: true })
}

/** Rétablit un lancement antérieur, sans recalcul (DEV-2150). */
async function onRetablirEtude(ids: string[]): Promise<void> {
    await optimTurpeV2Store.retablirLancement(ids)
}

/** Sélection du scénario d'arbitrage (Z3) — bascule l'étude affichée (Z1 / Z5). */
function onSelectScenario(scenario: "sans" | "avec"): void {
    if (isMock.value) return
    optimTurpeV2Store.setScenarioRetenu(scenario)
}

/**
 * « Compléter » (L29) : ouvrir le champ à saisir dans « Affiner l'étude ».
 *
 * On repasse par `null` avant de poser l'ancre, pour qu'un deuxième clic sur le
 * même levier rouvre le panneau au lieu de ne rien faire (la valeur n'aurait
 * pas changé).
 */
async function onCompleter(ancre: AncreAffiner): Promise<void> {
    // Le geste conduit au cadre, qui porte le champ (lot A, DEV-2284).
    ouvertures.cadre = true
    // Revue 2 en production (I80, DEV-2404) : sur un résultat, le formulaire
    // vit dans la rangée « Hypothèses », sous « Modifier les hypothèses ».
    // Repliées, le champ restait caché et rien ne se passait à l'écran : les
    // deux s'ouvrent d'abord, puis le formulaire amène le champ à l'écran
    // (défilement du conteneur de la page) et lui donne le focus.
    if (getModeCadre.value === "etude") {
        detailsOuverts.ajuster = true
        ouvrirRangee("hypotheses")
    }
    ancreACompleter.value = null
    await nextTick()
    ancreACompleter.value = ancre
}

/**
 * « Lever cette limite » (A7, L2) depuis la ligne du raccordement du registre.
 *
 * La levée part comme une surcharge ordinaire : le moteur la trace, la sert au
 * statut `levee` et le registre la relit « levée par le client ». La page ne
 * décide jamais seule de lever une borne.
 */
async function onLeverRaccordement(): Promise<void> {
    await onRelancer({ optimisation: { bornes: { raccordement_levee: true } } })
}

/**
 * Aperçu du rapport (DEV-1914) : on montre LE document composé par courtage, pas une autre
 * mise en page du navigateur. C'est le même appel, donc la même URL, que le téléchargement :
 * ce que le client regarde à l'écran est exactement ce qu'il transmettra.
 */
async function onRapport(): Promise<void> {
    apercuCharge.value = false
    isRapportVisible.value = true
    if (optimTurpeV2Store.reportUrl) return
    await optimTurpeV2Store.downloadReport()
    if (optimTurpeV2Store.rapportIndisponible) isRapportVisible.value = false
}

/**
 * Télécharge le rapport PDF (DEV-1914). La marque (nom et logo du courtier) est résolue par
 * courtage depuis le scope de la société : la page n'a plus à la transmettre, et le document
 * porte la même marque quel que soit le canal qui le demande. No-op en mock.
 */
async function onDownloadPdf(): Promise<void> {
    const url =
        optimTurpeV2Store.reportUrl ??
        (await optimTurpeV2Store.downloadReport())
    if (url) {
        track({
            event: "Optim V2 Report Downloaded",
            payload: {
                building_id: buildingId(),
                analyse_id: getCurrentEtude.value?._id ?? "",
            },
        })
        window.open(url, "_blank", "noopener")
    }
}

// ================================
// LIFECYCLE
// ================================
onMounted(() => {
    track({
        event: "Optim V2 Page Opened",
        payload: {
            building_id: buildingId(),
            source_id: optimTurpeV2Store.currentSourceId || undefined,
            source_segment: segmentPourMesure(),
        },
    })
    if (isMock.value) {
        // Une étude déjà positionnée (décorateur de story) n'est JAMAIS écrasée :
        // c'est ce qui faisait rendre la même fixture C4 aux neuf stories.
        if (optimTurpeV2Store.currentEtude) {
            selectedEtudeId.value =
                optimTurpeV2Store.currentEtude._id ?? selectedEtudeId.value
            return
        }
        optimTurpeV2Store.setMockEtude(selectedEtudeId.value)
        return
    }
    // Le périmètre du moteur, une fois. Son absence n'est pas une panne.
    optimTurpeV2Store.chargerPerimetre()
    syncSourceFromRoute()
})

/**
 * Mesure l'issue de l'étude affichée : son état SERVI, ou le refus nommé.
 *
 * On mesure l'issue et non le lancement : c'est elle qui dit si le client a eu sa
 * réponse, et c'est le seul moyen de compter les refus par leur cause vraie.
 */
watch(
    () => getCurrentEtude.value,
    (etude) => {
        if (!etude?._id || etudesMesurees.has(etude._id)) return
        etudesMesurees.add(etude._id)
        const echec = lireEchec(etude)
        if (echec) {
            track({
                event: "Optim V2 Study Refused",
                payload: {
                    building_id: buildingId(),
                    source_id: optimTurpeV2Store.currentSourceId || undefined,
                    source_segment: segmentPourMesure(),
                    cause: echec.code ?? "cause-non-servie",
                    levier: echec.levier ?? undefined,
                },
            })
            return
        }
        const chiffre = lireChiffre(etude)
        track({
            event: "Optim V2 Result Served",
            payload: {
                building_id: buildingId(),
                source_id: optimTurpeV2Store.currentSourceId,
                source_segment: segmentPourMesure(),
                etat: chiffre.etat ?? "non_disponible",
                niveau_donnees: getNiveauDonnees(etude),
                montant_servi: chiffre.montantServi ?? undefined,
            },
        })
    }
)

/**
 * R4 : la route change de bâtiment sans démonter la page. Le magasin se vide,
 * la visite repart de zéro sur le nouveau bâtiment.
 */
watch(
    () => route.params._id_building,
    (nouveau, ancien) => {
        if (isMock.value || nouveau === ancien) return
        optimTurpeV2Store.entrerSurBatiment(buildingId())
        sourceOuverteParLaVisite = null
        sourcesEtudiees = null
        etudeAilleurs.value = null
        syncSourceFromRoute()
    }
)

// En réel : (re)sync quand les compteurs du bâtiment arrivent (chargement async).
watch(
    () => buildingStore.getMeters,
    () => {
        if (!isMock.value) syncSourceFromRoute()
    }
)

/**
 * Quitter la page abandonne le run en cours (DEV-1583, revue 03/09 correction 4).
 * Sans ça, une relecture d'après panne continuait jusqu'à 90 s après le départ,
 * émettait jusqu'à 18 `GET /analyses` et réécrivait le store Pinia, qui survit à
 * la navigation. On abandonne sans vider le store : le résultat déjà affiché reste
 * disponible au retour, sans relancer d'étude.
 */
onUnmounted(() => {
    arreterGardeApercu()
    optimTurpeV2Store.abandonnerRunEnCours()
})
</script>

<template>
    <!-- ================================ -->
    <!-- PAGE — Optim TURPE (réel via api-metier ; mock-first en Storybook) -->
    <!-- v1 recentrée (DEV-783) : Résultat + Preuve + Relance. Leviers self-service -->
    <!-- (cadrage surchargeable, pricer manuel) reportés en v1.1 une fois câblés. -->
    <!-- ================================ -->
    <div class="page page-building-optim-turpe-v2">
        <HeaderPageBuilding
            :title="t('title')"
            class-header="flex flex-wrap items-center justify-between gap-x-4 gap-y-2"
        >
            <!-- DEV-2311, décision du 07/10 (pistes v0.4, carte 2, A1) : la
                 page d'aide se voit dans l'en-tête, en bouton secondaire. Un
                 lien (href) sous la forme d'un bouton : ouverture dans un
                 nouvel onglet possible, même destination qu'avant. -->
            <router-link v-slot="{ href, navigate }" :to="getRouteAide" custom>
                <!-- Série UX du témoin (15/10/2026, 2.2) : un geste de
                     navigation, de niveau 3 (lien souligné). -->
                <BaseButton
                    v-bind="NIVEAU_3"
                    as="a"
                    :href="href"
                    :label="t('comprendre')"
                    icon="pi pi-book"
                    class="shrink-0"
                    data-testid="optim-turpe-v2-lien-aide"
                    @click="navigate"
                />
            </router-link>
        </HeaderPageBuilding>
        <!-- Maquette finale du 15/10/2026 : le sous-titre et la ligne du
             compteur ne s'affichent plus sur un résultat (la carte de tête et
             la ligne de périmètre les portent, R8, M11). -->
        <p
            v-if="getModeCadre !== 'etude'"
            class="-mt-2 mb-3 text-sm text-primary-600"
            data-testid="optim-turpe-v2-sous-titre"
        >
            {{ t("sousTitre") }}
        </p>

        <div class="flex flex-col gap-6">
            <!-- Sélecteur de compteur ; sur un résultat, seulement quand le
                 bâtiment a plusieurs compteurs. -->
            <div
                v-if="getModeCadre !== 'etude' || getHasMultipleMeters"
                class="flex flex-wrap items-end justify-between gap-4"
            >
                <div class="flex min-w-72 flex-col gap-2">
                    <p class="text-xs text-primary-600">
                        {{ t("selectorLabel") }}
                    </p>
                    <BaseSelect
                        v-if="getHasMultipleMeters"
                        :model-value="getSelectedValue"
                        :options="getMeterOptions"
                        option-label="label"
                        option-value="value"
                        @update:model-value="onSelectMeter"
                    />
                    <p
                        v-else-if="getSingleMeterLabel"
                        class="text-sm font-medium text-primary-900"
                    >
                        {{ getSingleMeterLabel }}
                    </p>
                </div>
            </div>

            <!-- Segment hors périmètre, dit AVANT le clic, avec le libellé servi
                 par le moteur (A16). Sur la ligne du compteur, pas en bannière. -->
            <p
                v-if="getHorsPerimetreLabel"
                class="text-sm text-[var(--p-orange-700)]"
            >
                {{ getHorsPerimetreLabel }}
            </p>
            <!-- DEV-2158 : le catalogue des populations hors périmètre (producteur
                 pur…) n'est PLUS imprimé sous le compteur. Le fait propre au
                 compteur arrive par le refus nommé de son étude. -->

            <!-- Le bandeau de décision, collant, et son fil (D3). -->
            <!-- Maquette finale du 15/10/2026 : plus de barre collante sur un
                 résultat (la carte de tête dit déjà tout) ; elle reste sur un
                 refus, au rendu d'avant. -->
            <OptimTurpeV2Bandeau
                v-if="
                    !getIsLoading &&
                    getModeCadre === 'refus' &&
                    getValeursBandeau.length > 0
                "
                :valeurs="getValeursBandeau"
                :etapes="getEtapesDuFil"
                :tout-ouvert="getToutOuvert"
                @aller="onAller"
                @tout="onTout"
            />

            <!-- Calcul mis en file par le moteur : l'écran le DIT, au lieu
                 d'afficher une étude vide comme un résultat (DEV-1529). -->
            <!-- K1 (14/10/2026) : pendant le calcul, la carte d'attente le dit
                 seule ; le bandeau ne la redouble pas. -->
            <BaseMessage
                v-if="getMessageAttente && !getIsLoading"
                severity="info"
            >
                {{ getMessageAttente }}
            </BaseMessage>

            <BaseMessage v-if="getError && !getRefus" severity="error">
                {{ getError }}
                <!-- L'aide de l'application, à la place de « signalez au
                     support » (DEV-2311). -->
                <OptimTurpeV2Aide class="mt-1" />
            </BaseMessage>

            <!-- Aucun compteur électrique sur le bâtiment (réel) -->
            <BaseMessage
                v-else-if="
                    !isMock && getMeterOptions.length === 0 && !getIsLoading
                "
                severity="info"
            >
                {{ t("noMeter") }}
            </BaseMessage>

            <!-- Chargement « vivant » (étapes + squelette) pendant le calcul ~15-30 s -->
            <OptimTurpeV2Loading
                v-else-if="getIsLoading"
                :key="optimTurpeV2Store.currentSourceId"
                :lancement-accepte="
                    optimTurpeV2Store.isRunning
                        ? optimTurpeV2Store.lancementAccepte
                        : null
                "
            />

            <!-- LE PARCOURS (maquette allégée et parcours guidé du 07/10/2026).
                 Les paramètres de l'étude en tête : sans étude, les paramètres
                 recommandés et « Lancer l'étude recommandée » ; sur un refus,
                 ce qui bloque et les seuls paramètres qui le lèvent ; avec une
                 étude, la ligne de valeurs, repliée. Puis quatre temps, ouverts
                 sur une phrase ou un bloc de chiffres, leurs détails repliés en
                 lignes de rang 2 à en-tête porteur. -->
            <template
                v-else-if="
                    getCurrentEtude ||
                    getRefus ||
                    (!isMock && optimTurpeV2Store.currentSourceId)
                "
            >
                <!-- R4 : le bâtiment n'a aucune étude, la dernière du
                     compte est sur un autre bâtiment. -->
                <p
                    v-if="getModeCadre === 'entree' && etudeAilleurs"
                    class="text-sm text-surface-700"
                    data-testid="etude-ailleurs"
                >
                    {{ t("etudeAilleurs") }}
                    <router-link
                        :to="getLienEtudeAilleurs!"
                        class="font-medium text-primary-700 underline"
                    >
                        {{ etudeAilleurs.nom }}
                    </router-link>
                </p>
                <OptimTurpeV2Cadre
                    v-if="getModeCadre !== 'etude'"
                    :etude="getRefusAffiche ?? getCurrentEtude"
                    :mode="getModeCadre"
                    :ouvert="ouvertures.cadre"
                    :attente="attenteDuCadre"
                    :entete-calcul="getEnteteCalcul"
                    :details="detailsOuverts"
                    :tous-les-parametres="tousLesParametres"
                    :consentement-en-attente="getConsentementEnAttente"
                    :attente-donnees="getDonneesEnAttente"
                    :en-ouverture="getEnOuverture"
                    :lien-compteur="getLienCompteur"
                    :segment="getSelectedSegment || null"
                    @update:ouvert="ouvertures.cadre = $event"
                    @modifier="onModifierParametres"
                    @detail="onBasculerDetail"
                    @lancer="onLancerRecommandee"
                >
                    <template #entree>
                        <p
                            v-if="optimTurpeV2Store.historiqueError"
                            class="text-xs text-[var(--p-orange-700)]"
                        >
                            {{ optimTurpeV2Store.historiqueError }}
                        </p>
                    </template>
                    <!-- Ce qui bloque : le refus servi, sa cause et son remède. -->
                    <template #refus>
                        <OptimTurpeV2Echec
                            v-if="getRefusAffiche"
                            :etude="getRefusAffiche"
                            @completer="onCompleter"
                            @etudier-periode="onEtudierPeriode"
                        />
                        <p
                            v-if="avisPeriodeProposee && getRefusAffiche"
                            role="status"
                            class="text-sm text-[var(--p-red-600)]"
                            data-testid="optim-turpe-v2-avis-periode-proposee"
                        >
                            {{ avisPeriodeProposee }}
                        </p>
                        <!-- DEV-2439 : la recommandation d'avant n'est pas
                             perdue par un refus ; elle se rend en un clic. -->
                        <div
                            v-if="getRecommandationPrecedente"
                            class="flex flex-wrap items-center gap-3 text-sm text-primary-800"
                            data-testid="optim-turpe-v2-recommandation-precedente"
                        >
                            <span>
                                {{
                                    t("recommandationPrecedente.texte", {
                                        date: getRecommandationPrecedente.date,
                                    })
                                }}
                            </span>
                            <BaseButton
                                v-bind="NIVEAU_2"
                                :label="t('recommandationPrecedente.revenir')"
                                :loading="
                                    optimTurpeV2Store.geste?.type === 'retablir'
                                "
                                class="w-fit whitespace-nowrap"
                                :disabled="
                                    optimTurpeV2Store.geste !== null ||
                                    optimTurpeV2Store.isRunning
                                "
                                data-testid="optim-turpe-v2-revenir-recommandation"
                                @click="
                                    rendreRecommandation(
                                        getRecommandationPrecedente
                                    )
                                "
                            />
                            <p
                                v-if="optimTurpeV2Store.gesteErreur"
                                role="alert"
                                class="w-full text-xs text-[var(--p-orange-700)]"
                            >
                                {{ optimTurpeV2Store.gesteErreur }}
                            </p>
                        </div>
                        <!-- D31 : l'encart de retours, sous la cause, ouvert par
                             le lien, avant l'aide. -->
                        <OptimTurpeV2Retours
                            v-if="retours.visible && getIssueRetours"
                            v-model:ouvert="retours.ouvert"
                            :etude-id="getIssueRetours.etudeId"
                            :variante="getIssueRetours.variante"
                            :etat="getIssueRetours.etat"
                            :envoi="getEnvoiRetours"
                            @envoye="onAvisEnvoye"
                        />
                        <!-- L'aide de l'application, sous la cause (DEV-2311). -->
                        <OptimTurpeV2Aide />
                    </template>
                    <!-- « Ce qui a servi au calcul » : le registre des entrées. -->
                    <template #calcul>
                        <OptimTurpeV2Registre
                            v-if="getCurrentEtude"
                            :etude="getCurrentEtude"
                            integre
                            @completer="onCompleter"
                            @lever-raccordement="onLeverRaccordement"
                        />
                    </template>
                    <!-- « Ajuster l'étude » ; sur un refus, ses seuls remèdes ;
                         sans étude (Q0), avant le premier lancement. -->
                    <OptimTurpeV2Affiner
                        v-if="getEtudeReglages || getModeCadre === 'entree'"
                        :key="optimTurpeV2Store.currentSourceId"
                        :etude="getEtudeReglages ?? null"
                        :segment-compteur="getSelectedSegment || null"
                        :ancre="ancreACompleter"
                        :periode="
                            getRefus ? (getRefus.inputs?.data ?? null) : null
                        "
                        :refus="getRefus"
                        :details="detailsOuverts"
                        :remedes="getModeCadre === 'refus' ? getRemedes : null"
                        :actualisable="false"
                        :relancable="getModeCadre === 'refus'"
                        :donnees-manquantes="
                            getModeCadre === 'entree'
                                ? getDonneesManquantes
                                : null
                        "
                        ouvert
                        @relancer="onRelancerDuCadre"
                        @relancer-recommande="onRelancerRecommande"
                        @actualiser="onLancerDuJour"
                        @detail="onBasculerDetail"
                        @update:attente="attenteDuCadre = $event"
                    />
                </OptimTurpeV2Cadre>

                <!-- DEV-2412 : sans étude affichable, une étude restée en
                     attente au delà de quinze minutes ne disparaît pas ; la
                     liste la montre, datée, avec « Relancer ». -->
                <OptimTurpeV2Section
                    v-if="getModeCadre === 'entree' && getAttenteAbandonnee"
                    id="acc-etudes"
                    :entete="getEnteteEtudes"
                    :rang="1"
                    :ouvert="detailsOuverts.etudes !== false"
                    data-testid="optim-turpe-v2-etudes-en-attente"
                    @update:ouvert="onBasculerDetail('etudes', $event)"
                >
                    <OptimTurpeV2Historique
                        :etudes="optimTurpeV2Store.historique"
                        :etude-courante-id="null"
                        :suppression-en-cours="
                            optimTurpeV2Store.isSuppressionEnCours
                        "
                        :geste="optimTurpeV2Store.geste"
                        :geste-erreur="optimTurpeV2Store.gesteErreur"
                        :calcul-en-cours="optimTurpeV2Store.isRunning"
                        :tronquee="optimTurpeV2Store.historiqueTronque"
                        @ouvrir="onOuvrirEtude"
                        @supprimer="onSupprimerEtude"
                        @retablir="onRetablirEtude"
                        @relancer="onRelancerEnAttente"
                    />
                </OptimTurpeV2Section>

                <!-- ================================ -->
                <!-- LE RÉSULTAT (maquette finale du 15/10/2026, DEV-2404) : la
                     carte de tête, le graphique des puissances visible au
                     repos, puis le sommaire en trois accordéons, huit rangées
                     repliées dont l'en-tête donne déjà la conclusion. -->
                <!-- ================================ -->
                <div
                    v-if="getModeCadre === 'etude' && getCurrentEtude"
                    id="m-t1"
                    class="flex scroll-mt-6 flex-col gap-6"
                    data-testid="optim-turpe-v2-resultat"
                >
                    <OptimTurpeV2CarteDeTete
                        v-if="getCarte"
                        :etude="getCurrentEtude"
                        :carte="getCarte"
                        :pdl="getPdlAffiche"
                        :date="
                            getCalculeeLe === '—' ? getDateEtude : getCalculeeLe
                        "
                        :rapport-proposable="
                            getRapportProposable &&
                            !optimTurpeV2Store.rapportIndisponible &&
                            !isMock
                        "
                        :rapport-en-cours="optimTurpeV2Store.isReporting"
                        :relancable="!isMock"
                        @nouvelle-etude="onLancerDuJour"
                        @chiffrer-autres="onChiffrerAutres"
                        @chiffrer-periode="onRelancerPeriode"
                        @voir-recommandation="onVoirRecommandation"
                        @retenir-scenario="onSelectScenario"
                        @modifier-hypotheses="onModifierHypotheses"
                        @preparer-demande="onPreparerDemande"
                        @voir-resume="onVoirResume"
                        @corriger="onCompleter"
                        @telecharger="onDownloadPdf"
                    />
                    <p
                        v-if="avisRecommandation"
                        role="status"
                        class="text-sm text-primary-800"
                        data-testid="optim-turpe-v2-avis-recommandation"
                    >
                        {{ avisRecommandation }}
                    </p>
                    <p
                        v-if="avisPeriodeProposee && !getRefusAffiche"
                        role="status"
                        class="text-sm text-[var(--p-red-600)]"
                        data-testid="optim-turpe-v2-avis-periode-proposee"
                    >
                        {{ avisPeriodeProposee }}
                    </p>
                    <OptimTurpeV2Conclusion
                        :sous-la-carte="Boolean(getCarte)"
                        :periode-dans-la-carte="
                            getCarte?.action === 'chiffrer_periode'
                        "
                        :etude="getCurrentEtude"
                        :sans="optimTurpeV2Store.etudeSansCmdps"
                        :avec="optimTurpeV2Store.etudeAvecCmdps"
                        :option-chiffree="getScenarioNonRecommande"
                        @relancer-periode="onRelancerPeriode"
                        @choisir="onAller('m-t2')"
                    />

                    <div
                        class="flex flex-col gap-4"
                        data-testid="optim-turpe-v2-sommaire"
                    >
                        <BaseAccordion
                            v-for="groupe in getGroupesDuSommaire"
                            :key="groupe.cle"
                            v-model:value="ouvertes"
                            multiple
                            :data-testid="`optim-turpe-v2-groupe-${groupe.cle}`"
                        >
                            <BaseAccordionPanel
                                v-for="rangee in groupe.rangees"
                                :id="rangee.id"
                                :key="rangee.cle"
                                :value="rangee.cle"
                                class="scroll-mt-6"
                                data-forme="repliee"
                                :data-ouvert="
                                    ouvertes.includes(rangee.cle) ? '1' : '0'
                                "
                                :data-testid="`optim-turpe-v2-section-${rangee.id}`"
                            >
                                <BaseAccordionHeader>
                                    <span
                                        class="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-6 gap-y-1 text-left"
                                        data-testid="optim-turpe-v2-section-entete"
                                    >
                                        <span
                                            class="inline-flex items-center gap-2 text-lg font-semibold text-primary-700 lg:shrink-0 lg:basis-[29rem]"
                                            >{{ rangee.titre
                                            }}<BaseBadge
                                                v-if="rangee.compte"
                                                :value="rangee.compte"
                                                severity="secondary"
                                        /></span>
                                        <span
                                            v-if="rangee.resume"
                                            class="text-sm font-normal"
                                            :class="
                                                rangee.orange
                                                    ? 'text-[var(--p-orange-700)]'
                                                    : 'text-surface-700'
                                            "
                                            data-testid="optim-turpe-v2-section-resume"
                                            ><OptimTurpeV2TexteSigne
                                                :texte="rangee.resume"
                                        /></span>
                                    </span>
                                </BaseAccordionHeader>
                                <BaseAccordionContent>
                                    <div class="flex flex-col gap-4 text-sm">
                                        <!-- Montant par composante. -->
                                        <OptimTurpeV2Decomposition
                                            v-if="rangee.cle === 'composantes'"
                                            :etude="getCurrentEtude"
                                        />
                                        <!-- Dépassements : les deux scénarios. -->
                                        <template
                                            v-else-if="
                                                rangee.cle === 'depassements'
                                            "
                                        >
                                            <OptimTurpeV2Arbitrage
                                                v-if="
                                                    optimTurpeV2Store.getArbitragePertinent
                                                "
                                                :etude-sans="
                                                    optimTurpeV2Store.etudeSansCmdps
                                                "
                                                :etude-avec="
                                                    optimTurpeV2Store.etudeAvecCmdps
                                                "
                                                :retenu="
                                                    optimTurpeV2Store.scenarioRetenu
                                                "
                                                :detail-ouvert="
                                                    detailsOuverts.scenarios ===
                                                    true
                                                "
                                                @select="onSelectScenario"
                                                @detail="
                                                    onBasculerDetail(
                                                        'scenarios',
                                                        $event
                                                    )
                                                "
                                            />
                                            <p
                                                v-else
                                                class="text-surface-700"
                                                data-testid="optim-turpe-v2-depassements-question"
                                            >
                                                {{ getPresentationT2.question }}
                                            </p>
                                            <!-- Erreur du scénario : hors de la chaîne v-if/v-else de
                                                 l'arbitrage, sinon la question s'afficherait à côté
                                                 des tuiles. -->
                                            <p
                                                v-if="
                                                    optimTurpeV2Store.getArbitragePertinent &&
                                                    optimTurpeV2Store.scenarioErreur
                                                "
                                                role="alert"
                                                class="text-sm text-[var(--p-orange-700)]"
                                                data-testid="optim-turpe-v2-scenario-erreur"
                                            >
                                                {{
                                                    optimTurpeV2Store.scenarioErreur
                                                }}
                                            </p>
                                        </template>
                                        <!-- Pourquoi ces puissances : la
                                             formule, le raccordement, la
                                             règle de croissance, puis la
                                             preuve. -->
                                        <template
                                            v-else-if="
                                                rangee.cle === 'pourquoi'
                                            "
                                        >
                                            <dl
                                                v-if="
                                                    getLignesDuPourquoi.length
                                                "
                                                class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-[auto_minmax(0,1fr)]"
                                                data-testid="optim-turpe-v2-trois-lignes"
                                            >
                                                <template
                                                    v-for="ligne in getLignesDuPourquoi"
                                                    :key="ligne.testid"
                                                >
                                                    <dt
                                                        class="text-surface-500"
                                                    >
                                                        {{ ligne.libelle }}
                                                    </dt>
                                                    <dd
                                                        :class="
                                                            ligne.change
                                                                ? 'font-semibold text-primary-900'
                                                                : 'text-surface-800'
                                                        "
                                                        :data-testid="
                                                            ligne.testid
                                                        "
                                                        :title="ligne.titre"
                                                    >
                                                        {{ ligne.texte }}
                                                    </dd>
                                                </template>
                                            </dl>
                                            <ul
                                                v-if="getPucesDuPourquoi.length"
                                                class="mt-2 list-disc space-y-1 pl-5 text-sm text-surface-800"
                                                data-testid="optim-turpe-v2-puces-pourquoi"
                                            >
                                                <li
                                                    v-for="puce in getPucesDuPourquoi"
                                                    :key="puce"
                                                >
                                                    {{ puce }}
                                                </li>
                                            </ul>
                                            <OptimTurpeV2Preuve
                                                :etude="getCurrentEtude"
                                                :enable-serie="!isMock"
                                                :explication="getExplication"
                                            />
                                        </template>
                                        <!-- Points à vérifier (M8). -->
                                        <OptimTurpeV2PointsAVerifier
                                            v-else-if="rangee.cle === 'points'"
                                            :points="getPointsAVerifier"
                                            @corriger="onCompleter"
                                        />
                                        <!-- Hypothèses de l'étude : valeurs,
                                             provenance, « Modifier les
                                             hypothèses ». -->
                                        <OptimTurpeV2Cadre
                                            v-else-if="
                                                rangee.cle === 'hypotheses'
                                            "
                                            :etude="getCurrentEtude"
                                            mode="etude"
                                            :ouvert="ouvertures.cadre"
                                            :attente="attenteDuCadre"
                                            :entete-calcul="getEnteteCalcul"
                                            :details="detailsOuverts"
                                            :segment="
                                                getSelectedSegment || null
                                            "
                                            @update:ouvert="
                                                ouvertures.cadre = $event
                                            "
                                            @modifier="onModifierParametres"
                                            @detail="onBasculerDetail"
                                        >
                                            <template #calcul>
                                                <OptimTurpeV2Registre
                                                    :etude="getCurrentEtude"
                                                    integre
                                                    @completer="onCompleter"
                                                    @lever-raccordement="
                                                        onLeverRaccordement
                                                    "
                                                />
                                            </template>
                                            <OptimTurpeV2Affiner
                                                v-if="getEtudeReglages"
                                                :key="
                                                    optimTurpeV2Store.currentSourceId
                                                "
                                                :etude="
                                                    getEtudeReglages ?? null
                                                "
                                                :segment-compteur="
                                                    getSelectedSegment || null
                                                "
                                                :ancre="ancreACompleter"
                                                :periode="
                                                    getRefus
                                                        ? (getRefus.inputs
                                                              ?.data ?? null)
                                                        : null
                                                "
                                                :refus="getRefus"
                                                :details="detailsOuverts"
                                                :remedes="null"
                                                :actualisable="
                                                    !isMock &&
                                                    Boolean(getCalculeeLe)
                                                "
                                                :relancable="false"
                                                ouvert
                                                @relancer="onRelancerDuCadre"
                                                @relancer-recommande="
                                                    onRelancerRecommande
                                                "
                                                @actualiser="onLancerDuJour"
                                                @detail="onBasculerDetail"
                                                @update:attente="
                                                    attenteDuCadre = $event
                                                "
                                            />
                                        </OptimTurpeV2Cadre>
                                        <!-- Comparaison avec l'ancienne
                                             version (D29). -->
                                        <OptimTurpeV2Comparaison
                                            v-else-if="
                                                rangee.cle === 'comparaison'
                                            "
                                            :comparaison="getComparaisonV1"
                                            :building-id="
                                                String(
                                                    route.params._id_building ??
                                                        ''
                                                )
                                            "
                                            :source-id="
                                                optimTurpeV2Store.currentSourceId
                                            "
                                            @choisir="onAller('m-t2')"
                                        />
                                        <!-- Rapport et demande (Q36). -->
                                        <template
                                            v-else-if="rangee.cle === 'rapport'"
                                        >
                                            <BaseMessage
                                                v-if="
                                                    optimTurpeV2Store.reportError &&
                                                    !isRapportVisible
                                                "
                                                severity="error"
                                            >
                                                {{
                                                    optimTurpeV2Store.reportError
                                                }}
                                            </BaseMessage>
                                            <!-- Contrat v1.2 §7.4 (maquette
                                                 2c) : la demande, le rapport,
                                                 les limites, le résumé. -->
                                            <OptimTurpeV2RapportEtDemande
                                                :etude="getCurrentEtude"
                                                :carte="getCarte"
                                                :pdl="
                                                    getCurrentEtude.source
                                                        ?.idsource ?? null
                                                "
                                                :site="getSite"
                                                :date="getDateEtude"
                                                :signataire="getSignataire"
                                                :societe="getSociete"
                                                :valeurs-par-defaut="
                                                    getValeursParDefaut
                                                "
                                                :courriel-ouvert="
                                                    courrielOuvert
                                                "
                                                :resume-ouvert="
                                                    ouvertures.remise
                                                "
                                                :limites-ouvertes="
                                                    detailsOuverts.limites ===
                                                    true
                                                "
                                                :rapport-proposable="
                                                    getRapportProposable
                                                "
                                                :rapport-indisponible="
                                                    optimTurpeV2Store.rapportIndisponible
                                                "
                                                :rapport-en-cours="
                                                    optimTurpeV2Store.isReporting
                                                "
                                                :rapport-erreur="
                                                    optimTurpeV2Store.reportError &&
                                                    !isRapportVisible
                                                        ? optimTurpeV2Store.reportError
                                                        : null
                                                "
                                                :demonstration="isMock"
                                                @update:courriel-ouvert="
                                                    courrielOuvert = $event
                                                "
                                                @update:resume-ouvert="
                                                    onBasculerTemps(
                                                        'remise',
                                                        $event
                                                    )
                                                "
                                                @update:limites-ouvertes="
                                                    onBasculerDetail(
                                                        'limites',
                                                        $event
                                                    )
                                                "
                                                @telecharger="onDownloadPdf"
                                                @apercu="onRapport"
                                                @corriger="
                                                    onCompleter('raccordement')
                                                "
                                            />
                                        </template>
                                        <!-- Études de ce compteur (R7). -->
                                        <template
                                            v-else-if="rangee.cle === 'etudes'"
                                        >
                                            <OptimTurpeV2Historique
                                                :etudes="
                                                    optimTurpeV2Store.historique
                                                "
                                                :etude-courante-id="
                                                    getCurrentEtude?._id ?? null
                                                "
                                                :suppression-en-cours="
                                                    optimTurpeV2Store.isSuppressionEnCours
                                                "
                                                :geste="optimTurpeV2Store.geste"
                                                :geste-erreur="
                                                    optimTurpeV2Store.gesteErreur
                                                "
                                                :calcul-en-cours="
                                                    optimTurpeV2Store.isRunning
                                                "
                                                :tronquee="
                                                    optimTurpeV2Store.historiqueTronque
                                                "
                                                @ouvrir="onOuvrirEtude"
                                                @supprimer="onSupprimerEtude"
                                                @retablir="onRetablirEtude"
                                                @relancer="onRelancerEnAttente"
                                            />
                                            <p
                                                v-if="
                                                    optimTurpeV2Store.historiqueError
                                                "
                                                class="text-sm text-[var(--p-orange-700)]"
                                            >
                                                {{
                                                    optimTurpeV2Store.historiqueError
                                                }}
                                            </p>
                                        </template>
                                    </div>
                                </BaseAccordionContent>
                            </BaseAccordionPanel>
                        </BaseAccordion>
                    </div>
                </div>
                <!-- Échecs (mode refus) : le rendu d'avant la maquette finale,
                     inchangé (garde-fou du lot) : la section « Résultat » sans
                     objet, puis « Études de ce compteur ». Les sections
                     « Dépassements » et « Rapport et demande » sont absentes
                     sur un refus (A8). -->
                <template v-if="getModeCadre === 'refus' && getRefusAffiche">
                    <section
                        id="m-t1"
                        class="flex scroll-mt-36 flex-col gap-3 rounded-lg border border-surface-200 bg-white px-5 py-3"
                        aria-labelledby="m-t1-h"
                    >
                        <div class="flex flex-col">
                            <h2
                                id="m-t1-h"
                                class="text-base font-semibold text-primary-900"
                            >
                                {{ t("parcours.t1Titre") }}
                            </h2>
                            <p class="text-sm text-primary-600">
                                {{ t("parcours.t1SansObjet") }}
                            </p>
                        </div>
                    </section>
                    <OptimTurpeV2Section
                        id="acc-etudes"
                        :entete="getEnteteEtudes"
                        :rang="1"
                        :ouvert="detailsOuverts.etudes === true"
                        @update:ouvert="onBasculerDetail('etudes', $event)"
                    >
                        <OptimTurpeV2Historique
                            :etudes="optimTurpeV2Store.historique"
                            :etude-courante-id="
                                getRefus ? null : (getCurrentEtude?._id ?? null)
                            "
                            :suppression-en-cours="
                                optimTurpeV2Store.isSuppressionEnCours
                            "
                            :geste="optimTurpeV2Store.geste"
                            :geste-erreur="optimTurpeV2Store.gesteErreur"
                            :calcul-en-cours="optimTurpeV2Store.isRunning"
                            :tronquee="optimTurpeV2Store.historiqueTronque"
                            @ouvrir="onOuvrirEtude"
                            @supprimer="onSupprimerEtude"
                            @retablir="onRetablirEtude"
                            @relancer="onRelancerEnAttente"
                        />
                        <p
                            v-if="optimTurpeV2Store.historiqueError"
                            class="text-xs text-[var(--p-orange-700)]"
                        >
                            {{ optimTurpeV2Store.historiqueError }}
                        </p>
                    </OptimTurpeV2Section>
                </template>
                <!-- Pied : « Donner votre avis sur cette étude » (D31, niveau 3) ;
                     sur un refus, l'aide de l'application reste sous la cause. -->
                <div
                    v-if="getCurrentEtude || getRefus"
                    class="flex flex-wrap items-baseline gap-x-4 gap-y-1"
                >
                    <BaseButton
                        v-if="getIssueRetours"
                        v-bind="NIVEAU_3"
                        :label="TEXTES_RETOURS.lienPied"
                        data-testid="optim-turpe-v2-retours-lien"
                        @click="onDonnerAvis"
                    />
                    <!-- En marque blanche, sans bouton « Aide », la phrase de
                         l'interlocuteur habituel reste le seul recours. -->
                    <OptimTurpeV2Aide
                        v-if="getModeCadre === 'etude'"
                        compact
                        interlocuteur-seul
                    />
                </div>
                <!-- D31 : l'encart de retours, sous le pied, ouvert par le lien. -->
                <OptimTurpeV2Retours
                    v-if="
                        getModeCadre === 'etude' &&
                        retours.visible &&
                        getIssueRetours
                    "
                    v-model:ouvert="retours.ouvert"
                    :etude-id="getIssueRetours.etudeId"
                    :variante="getIssueRetours.variante"
                    :etat="getIssueRetours.etat"
                    :envoi="getEnvoiRetours"
                    @envoye="onAvisEnvoye"
                />
                <OptimTurpeV2DossierEquipe
                    v-if="getRefusAffiche ?? getCurrentEtude"
                    :etude="(getRefusAffiche ?? getCurrentEtude)!"
                />
            </template>

            <BaseMessage v-else severity="info">{{ t("empty") }}</BaseMessage>
        </div>

        <!-- Aperçu rapport (cartouche white-label) -->
        <!-- Revue témoin du 15/10/2026 (D26) : un aperçu plus large et plus
             haut, l'état de chargement superposé jusqu'au chargement du
             document, et un pied qui garde le geste quand l'aperçu ne
             s'affiche pas (télécharger, ou ouvrir dans un nouvel onglet). -->
        <BaseDialog
            v-model:visible="isRapportVisible"
            modal
            :header="t('rapportDialogTitle')"
            :style="{ width: 'min(72rem, 95vw)' }"
        >
            <!-- Le document lui-même, tel que courtage l'a composé et rendu. Aucun chiffre
                 n'est recomposé ici : il n'y a plus de seconde mise en page à maintenir. -->
            <div
                v-if="optimTurpeV2Store.reportUrl"
                class="relative h-[80vh] w-full"
                data-testid="optim-turpe-v2-apercu"
            >
                <iframe
                    :src="optimTurpeV2Store.reportUrl"
                    :title="t('rapportApercuTitre')"
                    class="h-full w-full border-0"
                    data-testid="optim-turpe-v2-apercu-document"
                    @load="apercuCharge = true"
                />
                <p
                    v-if="!apercuCharge && !apercuEnRetard"
                    class="absolute inset-0 flex items-center justify-center bg-white text-sm text-primary-700"
                    role="status"
                    data-testid="optim-turpe-v2-apercu-chargement"
                >
                    {{ t("rapportApercuChargement") }}
                </p>
                <p
                    v-else-if="!apercuCharge && apercuEnRetard"
                    class="absolute inset-x-0 top-0 bg-white p-3 text-sm text-primary-700"
                    role="status"
                    data-testid="optim-turpe-v2-apercu-en-retard"
                >
                    {{ t("rapportApercuEnRetard") }}
                </p>
            </div>
            <p
                v-else-if="optimTurpeV2Store.isReporting"
                class="text-sm"
                role="status"
            >
                {{ t("rapportApercuChargement") }}
            </p>
            <BaseMessage
                v-else-if="optimTurpeV2Store.reportError"
                severity="error"
            >
                {{ optimTurpeV2Store.reportError }}
            </BaseMessage>
            <template #footer>
                <div
                    class="flex flex-wrap items-center gap-3"
                    data-testid="optim-turpe-v2-apercu-pied"
                >
                    <BaseButton
                        :label="t('actions.telecharger')"
                        icon="pi pi-file-pdf"
                        size="small"
                        :disabled="isMock || optimTurpeV2Store.isReporting"
                        data-testid="optim-turpe-v2-apercu-telecharger"
                        @click="onDownloadPdf"
                    />
                    <a
                        v-if="optimTurpeV2Store.reportUrl"
                        :href="optimTurpeV2Store.reportUrl"
                        target="_blank"
                        rel="noopener"
                        class="text-sm text-primary-700 underline"
                        data-testid="optim-turpe-v2-apercu-onglet"
                        >{{ t("rapportOuvrirOnglet") }}</a
                    >
                </div>
            </template>
        </BaseDialog>
    </div>
</template>

<style scoped>
/* A3, L12, R11 (vague wording du 14/10/2026) : un bouton secondaire cliquable
   (contour ou texte : « Copier le résumé », « Modifier les hypothèses »,
   « Replier », C2 / C4 / C5…) ne se lit plus en gris clair ; le gris clair ne
   sert qu'à un bouton réellement inactif. Le style par défaut du thème met le
   texte en surface 500 sur un contour surface 200. */
.page-building-optim-turpe-v2
    :deep(.p-button-secondary.p-button-outlined:not(:disabled)) {
    color: var(--p-primary-800);
    border-color: var(--p-surface-400);
}
.page-building-optim-turpe-v2
    :deep(.p-button-secondary.p-button-text:not(:disabled)) {
    color: var(--p-primary-800);
}
</style>
